import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// Types for wallet transactions
export interface WalletTransaction {
    transaction_id: number;
    user: {
        user_id: number;
        name: string;
        email: string;
    };
    amount: number;
    transaction_type: 'deposit' | 'withdrawal';
    status: 'pending' | 'completed' | 'failed' | 'rejected';
    payment_method: string;
    payment_screenshot: string | null;
    transaction_reference: string;
    description: string;
    balance_before: number;
    balance_after: number;
    verified_by: number | null;
    verified_at: string | null;
    rejection_reason: string | null;
    created_at: string;
    updated_at: string;
}

export interface WalletTransactionsResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        transactions: WalletTransaction[];
        pagination: {
            current_page: number;
            total_pages: number;
            total_transactions: number;
            limit: number;
        };
    };
}

export interface WalletTransactionsParams {
    page?: number;
    limit?: number;
    status?: 'pending' | 'completed' | 'failed' | 'rejected' | 'all';
    transaction_type?: 'deposit' | 'withdrawal' | 'all';
}

// Contact Submissions Types
export interface ContactSubmission {
    id: number;
    name: string;
    email: string;
    phone: string;
    subject: string;
    message: string;
    status: 'pending' | 'replied' | 'resolved';
    is_read: boolean;
    admin_reply: string | null;
    replied_at: string | null;
    replied_by: number | null;
    notes: string | null;
    ip_address: string;
    user_agent: string;
    created_at: string;
    updated_at: string;
}

export interface ContactSubmissionsResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        submissions: ContactSubmission[];
        pagination: {
            page: number;
            limit: number;
            total: number;
            totalPages: number;
        };
        statistics: {
            pending: number;
            unread: number;
            total: number;
        };
    };
}

export interface ContactSubmissionsParams {
    page?: number;
    limit?: number;
    status?: 'pending' | 'replied' | 'resolved' | 'all';
    is_read?: boolean;
}

export interface ContactSubmissionResponse {
    success: boolean;
    status: number;
    message: string;
    data: ContactSubmission;
}

export interface UpdateContactSubmissionRequest {
    id: number;
    status: 'pending' | 'replied' | 'resolved';
    notes?: string;
}

export interface UpdateContactSubmissionResponse {
    success: boolean;
    status: number;
    message: string;
    data: ContactSubmission;
}

export interface DeleteContactSubmissionResponse {
    success: boolean;
    status: number;
    message: string;
}

export interface ApproveRejectWalletTransactionRequest {
    transaction_id: number;
    action: 'approve' | 'reject';
    rejection_reason?: string;
}

export interface ApproveRejectWalletTransactionResponse {
    success: boolean;
    status: number;
    message: string;
    data?: any;
}

export interface AddFundsRequest {
    user_id: number;
    amount: number;
    description: string;
}

export interface AddFundsResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        user_id: number;
        amount_added: number;
        new_balance: number;
        transaction_id: number;
    };
}

export interface WalletStatsResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        total_deposits: number;
        total_withdrawals: number;
        total_investments_from_wallet: number;
        pending_transactions: number;
        total_wallet_balance: number;
    };
}

// User-facing Wallet Types
export interface UserWalletBalanceResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        wallet_balance: number;
        currency_symbol: string;
        user_name: string;
        user_email: string;
    };
}

export interface UserWalletTransaction {
    transaction_id: number;
    amount: number;
    transaction_type: 'deposit' | 'withdrawal' | 'investment' | 'sponsorship' | 'booking' | 'marketplace';
    status: 'pending' | 'completed' | 'failed' | 'rejected';
    payment_method: string | null;
    payment_screenshot: string | null;
    transaction_reference: string | null;
    description: string;
    balance_before: number;
    balance_after: number;
    verified_by: string | null;
    verified_at: string | null;
    rejection_reason: string | null;
    created_at: string;
    updated_at: string;
}

export interface UserWalletTransactionsResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        transactions: UserWalletTransaction[];
        pagination: {
            current_page: number;
            total_pages: number;
            total_transactions: number;
            limit: number;
        };
    };
}

export interface UserWalletTransactionsParams {
    page?: number;
    limit?: number;
    transaction_type?: 'deposit' | 'withdrawal' | 'investment' | 'sponsorship' | 'booking' | 'marketplace' | 'all';
}

export interface AddFundsToWalletRequest {
    amount: number;
    payment_method: string;
    transaction_reference?: string;
    description?: string;
    payment_screenshot: File;
}

export interface AddFundsToWalletResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        transaction_id: number;
        amount: number;
        status: 'pending';
        message: string;
    };
}

export interface WithdrawFundsRequest {
    amount: number;
    description?: string;
}

export interface WithdrawFundsResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        transaction_id: number;
        amount: number;
        status: 'pending';
        message: string;
    };
}

export interface UserWalletStatsResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        total_deposits: number;
        total_withdrawals: number;
        total_investments_from_wallet: number;
        pending_transactions: number;
    };
}

// Payment Methods Types
export interface PaymentMethod {
    payment_gateway_options_id: number;
    payment_method_name: string;
    display_name: string;
    description: string;
    username?: string;
    email?: string;
    phone?: string;
    account_name?: string;
    qr_code?: string;
    instructions?: string;
    supported_currencies?: string[];
    minimum_amount?: number;
    maximum_amount?: number;
    is_default: boolean;
}

export interface PaymentMethodsResponse {
    success: boolean;
    status: number;
    message: string;
    data: PaymentMethod[];
}

export interface PaymentMethodDetailsResponse {
    success: boolean;
    status: number;
    message: string;
    data: PaymentMethod;
}

// Types for admin endpoints
export interface DashboardDetails {
    success: boolean;
    status: number;
    message: string;
    data: {
        total_users: number;
        total_investments: number;
        total_projects: number;
        total_amount_invested: number;
    };
}

export interface User {
    id: string;
    name: string;
    email: string;
    is_active: boolean;
    role: string;
    createdAt: string;
    lastLogin?: string;
}

export interface UserListResponse {
    success: boolean;
    status: number;
    message: string;
    data: Array<{
        user_id: number;
        name: string | null;
        email: string;
        mobile_number: string | null;
        show_password: string | null;
        password: string | null;
        profile_image: string | null;
        user_qr_code_image: string | null;
        jwt_token: string | null;
        reset_password_token: string | null;
        reset_password_token_expiry: string | null;
        verify_token: string | null;
        verify_token_expiry: string | null;
        is_verified: boolean;
        is_active: boolean;
        is_complete_profile: boolean;
        signup_method: string | null;
        otp: string;
        wallet_balance: string;
        selected_country: string | null;
        currency_symbol: string | null;
        fcm_token: string | null;
        total_investment: string;
        total_profit: string;
        invested_current_amount: string;
        is_deleted: boolean;
        deleted_at: string | null;
        created_at: string;
        updated_at: string;
    }>;
    total?: number;
    page?: number;
    limit?: number;
}

export interface UpdateUserStatusRequest {
    user_id: number;
    is_active: boolean;
}

export interface UpdateUserStatusResponse {
    success: boolean;
    message: string;
    user: User;
}

export interface RestoreUserRequest {
    user_id: number;
}

export interface RestoreUserResponse {
    success: boolean;
    message: string;
}

export interface Category {
    category_id: number;
    name: string;
    description?: string | null;
    created_at?: string;
    updated_at?: string;
}

export interface CategoryListResponse {
    success: boolean;
    status: number;
    message: string;
    data: Category[];
}

export interface CreateCategoryRequest {
    name: string;
    description?: string | null;
}

export interface UpdateCategoryRequest {
    category_id: number;
    name: string;
    description?: string | null;
}

export interface DeleteCategoryRequest {
    category_id: number;
}

export interface CategoryResponse {
    success: boolean;
    message: string;
    data?: Category;
}

// Bank Account Types
export interface BankAccount {
    bank_account_id: number;
    account_holder_name: string;
    account_number: string;
    bank_name: string;
    country_code: string;
    created_at: string;
    updated_at: string;
}

export interface BankAccountListResponse {
    success: boolean;
    status: number;
    message: string;
    data: BankAccount[];
}

export interface CreateBankAccountRequest {
    account_holder_name: string;
    bank_name: string;
    account_number: string;
    country_code: string;
}

export interface UpdateBankAccountRequest {
    bank_account_id: number;
    account_holder_name: string;
    bank_name: string;
    account_number: string;
    country_code: string;
}

export interface DeleteBankAccountRequest {
    bank_account_id: number;
}

export interface BankAccountResponse {
    success: boolean;
    message: string;
    data?: BankAccount;
}

// Admin Management Types
export interface Admin {
    admin_id: number;
    name: string;
    email: string;
    mobile_number: string;
    profile_image?: string;
    role: 'admin' | 'manager' | 'exclusive';
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface AdminListResponse {
    success: boolean;
    status: number;
    message: string;
    data: Admin[];
}

export interface CreateAdminRequest {
    name: string;
    email: string;
    password: string;
    mobile_number: string;
    role: 'admin' | 'manager' | 'exclusive';
}

export interface UpdateAdminRequest {
    admin_id: number;
    name: string;
    mobile_number: string;
    password: string;
    role: 'admin' | 'manager' | 'exclusive';
}

export interface DeleteAdminRequest {
    admin_id: number;
}

export interface AdminResponse {
    success: boolean;
    message: string;
    data?: Admin;
}

// Investment Types
export interface InvestmentUser {
    user_id: number;
    name: string | null;
    email: string;
    mobile_number: string | null;
    profile_image: string | null;
    is_verified: boolean;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface InvestmentProject {
    project_id: number;
    title: string;
    short_description: string;
    full_description: string;
    location: string;
    funding_goal: number;
    amount_raised: number | null;
    deadline: string;
    is_funded: boolean;
    status: string;
    created_at: string;
    updated_at: string;
}

export interface Investment {
    investment_id: number;
    projectId: number;
    amount_invested: string;
    payment_screenshot: string | null;
    status: 'pending' | 'approved' | 'rejected';
    expected_roi_amount: string;
    total_roi_expected: string;
    total_roi_received: string;
    verified_at: string | null;
    created_at: string;
    updated_at: string;
    user?: InvestmentUser;
    verified_by: string | null;
    project: InvestmentProject;
}

// User-facing Investment Types
export interface UserInvestment {
    investment_id: number;
    projectId: number;
    amount_invested: string;
    payment_screenshot: string | null;
    status: 'pending' | 'approved' | 'rejected';
    expected_roi_amount: string;
    total_roi_expected: string;
    total_roi_received: string;
    verified_at: string | null;
    created_at: string;
    updated_at: string;
    project: {
        project_id: number;
        title: string;
        short_description: string;
        full_description: string;
        location: string;
        currency_symbol: string;
        country_code: string;
        latitude: number;
        longitude: number;
        funding_goal: string;
        amount_raised: string;
        expected_roi_percentage: string;
        roi_duration_months: number;
        deadline: string;
        is_funded: boolean;
        status: string;
        created_at: string;
        updated_at: string;
        images?: Array<{
            project_images_id: number;
            image_url: string;
            created_at: string;
            updated_at: string;
        }>;
    };
    verified_by: string | null;
}

export interface UserInvestmentsResponse {
    success: boolean;
    status: number;
    message: string;
    data: UserInvestment[];
}

export interface UserInvestmentStats {
    total_investments: string;
    approved_investments: string;
    pending_investments: string;
    total_amount_invested: string;
    pending_amount: string;
    total_expected_roi: string;
    total_received_roi: string;
}

export interface UserInvestmentStatsResponse {
    success: boolean;
    status: number;
    message: string;
    data: UserInvestmentStats;
}

export interface CreateInvestmentRequest {
    project_id: number;
    amount_invested: number;
    file: File;
}

export interface CreateInvestmentResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        investment_id: number;
        projectId: number;
        amount_invested: number;
        payment_screenshot: string;
        status: 'pending';
        total_roi_expected: string;
        user: any;
        project: any;
        expected_roi_amount: string;
        total_roi_received: string;
        verified_at: string | null;
        created_at: string;
        updated_at: string;
    };
}

export interface CreateWalletInvestmentRequest {
    project_id: number;
    amount_invested: number;
}

export interface CreateWalletInvestmentResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        investment_id: number;
        amount_invested: number;
        project_title: string;
        wallet_balance_remaining: number;
        status: 'approved';
    };
}

export interface ProjectStatus {
    project_id: number;
    title: string;
    status: string;
    is_funded: boolean;
    funding_goal: string;
    amount_raised: string;
    can_invest: boolean;
}

export interface ProjectStatusResponse {
    success: boolean;
    status: number;
    message: string;
    data: ProjectStatus;
}

export interface UserProject {
    project_id: number;
    title: string;
    short_description: string;
    full_description: string;
    location: string;
    currency_symbol: string;
    country_code: string;
    latitude: number;
    longitude: number;
    funding_goal: string;
    amount_raised: string;
    expected_roi_percentage: string;
    roi_duration_months: number;
    deadline: string;
    is_funded: boolean;
    status: string;
    created_at: string;
    updated_at: string;
    category: {
        category_id: number;
        name: string;
        description: string | null;
        created_at: string;
        updated_at: string;
    };
    original_funding_goal: number;
    original_amount_raised: number;
    original_currency_code: string;
    original_currency_symbol: string;
    converted_funding_goal: number;
    converted_amount_raised: number;
    converted_currency_code: string;
    converted_currency_symbol: string;
    images: Array<{
        project_images_id: number;
        image_url: string;
        created_at: string;
        updated_at: string;
        project: any;
    }>;
    documents: Array<{
        project_documents_id: number;
        document_name: string;
        document_url: string;
        created_at: string;
        updated_at: string;
        project: any;
    }>;
    rules: {
        project_rules_id: number;
        auto_close_on_goal: boolean;
        max_allowed_investment: string;
        min_allowed_investment: string;
        created_at: string;
        updated_at: string;
        project: any;
    } | null;
}

export interface UserProjectsResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        projects: UserProject[];
        pagination: {
            current_page: number;
            total_pages: number;
            total_projects: number;
            limit: number;
        };
    };
}

export interface UserProjectsParams {
    page?: number;
    limit?: number;
    category_id?: number;
    search?: string;
}

export interface InvestmentListResponse {
    success: boolean;
    status: number;
    message: string;
    data: Investment[];
}

export interface ApproveRejectInvestmentRequest {
    project_id: number;
    investment_id: number;
    status: 'approved' | 'rejected';
}

export interface ApproveRejectInvestmentResponse {
    success: boolean;
    message: string;
    data?: Investment;
}

// Project Types
export interface CreateProjectRequest {
    title: string;
    short_description: string;
    full_description: string;
    category_id: number;
    location: string;
    latitude?: number;
    longitude?: number;
    funding_goal: number;
    deadline?: string;
    rules: {
        auto_close_on_goal: boolean;
        min_allowed_investment: number;
        max_allowed_investment: number;
    };
    status: 'draft' | 'active' | 'closed';
    expected_roi_percentage: number;
    roi_duration_months: number;
}

export interface ProjectImage {
    project_images_id: number;
    image_url: string;
    created_at: string;
    updated_at: string;
}

export interface ProjectDocument {
    project_documents_id: number;
    document_name: string;
    document_url: string;
    created_at: string;
    updated_at: string;
}

export interface ProjectRules {
    project_rules_id: number;
    auto_close_on_goal: boolean;
    max_allowed_investment: string;
    min_allowed_investment: string;
    created_at: string;
    updated_at: string;
}

export interface ProjectCategory {
    category_id: number;
    name: string;
    created_at: string;
    updated_at: string;
}

export interface Project {
    project_id: number;
    title: string;
    short_description: string;
    full_description: string;
    location: string;
    currency_symbol: string;
    country_code: string;
    latitude: number;
    longitude: number;
    funding_goal: number;
    amount_raised: number;
    expected_roi_percentage: string;
    roi_duration_months: number;
    deadline: string;
    is_funded: boolean;
    status: string;
    created_at: string;
    updated_at: string;
    category: ProjectCategory;
    images: ProjectImage[];
    documents: ProjectDocument[];
    rules: ProjectRules;
}

export interface ProjectListResponse {
    success: boolean;
    status: number;
    message: string;
    data: Project[];
}

// Job / Careers Types
export interface Job {
    id: number;
    title: string;
    location: string;
    type: 'Full-time' | 'Part-time' | 'Contract' | 'Internship';
    shortDescription: string;
    fullDescription: string;
    responsibilities?: string;
    requirements?: string;
    salary?: string;
    department?: string;
    isActive: boolean;
    created_at: string;
    updated_at: string;
}

export interface JobApplication {
    id: number;
    jobId: number;
    job?: Job;
    fullName: string;
    email: string;
    phone: string;
    resumeUrl: string;
    coverLetter?: string;
    created_at: string;
    updated_at: string;
}

export interface JobsResponse {
    success: boolean;
    status: number;
    message: string;
    data: Job[];
}

export interface JobResponse {
    success: boolean;
    status: number;
    message: string;
    data: Job;
}

export interface JobApplicationsResponse {
    success: boolean;
    status: number;
    message: string;
    data: JobApplication[];
}


export interface CreateProjectResponse {
    success: boolean;
    data: {
        project_id: number;
    };
    message?: string;
}

export interface AddProjectImagesRequest {
    project_id: number;
    images: File[];
}

export interface AddProjectImagesResponse {
    success: boolean;
    message: string;
}

export interface AddProjectDocumentsRequest {
    project_id: string;
    document_name: string;
    document: File;
}

export interface AddProjectDocumentsResponse {
    success: boolean;
    message: string;
}

export interface DeleteProjectRequest {
    project_id: number;
}

export interface DeleteProjectResponse {
    success: boolean;
    message: string;
}

// Location Search
export interface LocationSearchResponse {
    success: boolean;
    message: string;
    data: LocationSuggestion[];
}

export interface LocationSuggestion {
    place_id: string;
    description: string;
    lat: number;
    lng: number;
}

// Get Latitude and Longitude
export interface LatLongResponse {
    success: boolean;
    message: string;
    data: {
        latitude: number;
        longitude: number;
    };
}

// Get Project Details
export interface ProjectDetailsResponse {
    success: boolean;
    message: string;
    data: ProjectDetails;
}

export interface ProjectDetails {
    project_id: number;
    title: string;
    short_description: string;
    full_description: string;
    location: string;
    currency_symbol: string;
    latitude: number;
    longitude: number;
    funding_goal: number;
    amount_raised: number | null;
    expected_roi_percentage: string;
    roi_duration_months: number;
    deadline: string;
    is_funded: boolean;
    status: string;
    created_at: string;
    updated_at: string;
    category: {
        category_id: number;
        name: string;
        description: string | null;
        created_at: string;
        updated_at: string;
    };
    images: Array<{
        project_images_id: number;
        image_url: string;
        created_at: string;
        updated_at: string;
    }>;
    documents: Array<{
        project_documents_id: number;
        document_name: string;
        document_url: string;
        created_at: string;
        updated_at: string;
    }>;
}

// Blogs Management
export interface BlogsParams {
    page?: number;
    limit?: number;
    status?: string;
    category?: string;
    search?: string;
}

export interface BlogsResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        blogs: Blog[];
        pagination: {
            page: number;
            limit: number;
            total: number;
            totalPages: number;
        };
        statistics: {
            draft: number;
            published: number;
            featured: number;
            total: number;
        };
    };
}

export interface Blog {
    id: number;
    title: string;
    desc: string;
    content: string;
    category: string;
    author: string;
    image: string | null;
    tags: string[];
    seoKeywords: string[];
    readTime: number;
    views: number;
    rating: string;
    likes: number;
    comments: number;
    featured: boolean;
    status: 'draft' | 'published';
    slug: string;
    metaTitle: string;
    metaDescription: string;
    publishedBy: string;
    publishedAt: string;
    ip_address: string;
    user_agent: string;
    created_at: string;
    updated_at: string;
}

export interface BlogByIdResponse {
    success: boolean;
    status: number;
    message: string;
    data: Blog;
}

export interface BlogCreateResponse {
    success: boolean;
    status: number;
    message: string;
    data: Blog;
}

// Notification Management
export interface Notification {
    notification_id: number;
    title: string;
    message: string;
    notification_type: string;
    notification_for: string;
    created_at: string;
    schedule_at: string;
}

export interface NotificationListResponse {
    success: boolean;
    status: number;
    message: string;
    data: Notification[];
}

export interface NotificationResponse {
    success: boolean;
    status: number;
    message: string;
    data?: Notification;
}

export interface ScheduleNotificationRequest {
    title: string;
    message: string;
    schedule_at: string;
    notification_for: string;
    notification_type?: string;
}

export interface UpdateNotificationRequest {
    notification_id: number;
    title: string;
    message: string;
    schedule_at: string;
    notification_for: string;
    notification_type?: string;
}

// CTA Interfaces
export interface CTA {
    popup_id: number;
    title: string;
    description: string;
    button_text: string;
    href: string;
    file_type: 'image' | 'video';
    file?: string | null;
    thumbnail_image?: string | null;
    is_active?: boolean;
    created_at: string;
    updated_at: string;
}

export interface CTAListResponse {
    success: boolean;
    status: number;
    message: string;
    data: CTA[];
}

export interface CTAResponse {
    success: boolean;
    status: number;
    message: string;
    data?: CTA;
}

export interface CreateCTARequest {
    title: string;
    description: string;
    button_text: string;
    href: string;
    file_type: 'image' | 'video';
}

export interface UpdateCTARequest {
    id: number;
    title: string;
    description: string;
    button_text: string;
    href: string;
    file_type: 'image' | 'video';
}

// Testimonials Interfaces
export interface Testimonial {
    id: number;
    name: string;
    text: string;
    rating: number;
    location: string;
    profile: string;
    status: 'pending' | 'approved' | 'rejected';
    category: string;
    helpful: number;
    verified: boolean;
    featured: boolean;
    profile_image: string | null;
    ip_address: string;
    user_agent: string;
    created_at: string;
    updated_at: string;
}

export interface TestimonialsListResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        testimonials: Testimonial[];
        pagination: {
            page: number;
            limit: number;
            total: number;
            totalPages: number;
        };
        statistics: {
            pending: number;
            approved: number;
            featured: number;
            total: number;
        };
    };
}

export interface TestimonialResponse {
    success: boolean;
    status: number;
    message: string;
    data?: Testimonial;
}

export interface CreateTestimonialRequest {
    name: string;
    text: string;
    rating: number;
    location: string;
    profile: string;
    category: string;
    status: 'pending' | 'approved' | 'rejected';
    verified: boolean;
    featured: boolean;
    helpful: number;
}

export interface UpdateTestimonialRequest {
    id: number;
    name: string;
    text: string;
    rating: number;
    location: string;
    profile: string;
    category: string;
    status: 'pending' | 'approved' | 'rejected';
    verified: boolean;
    featured: boolean;
    helpful: number;
}

export interface PublicTestimonial {
    id: number;
    name: string;
    text: string;
    rating: number;
    location: string;
    profile: string;
    status: 'pending' | 'approved' | 'rejected';
    category: string;
    helpful: number;
    verified: boolean;
    featured: boolean;
    profile_image: string | null;
    ip_address: string;
    user_agent: string;
    created_at: string;
    updated_at: string;
}

export interface PublicTestimonialsResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        testimonials: PublicTestimonial[];
        pagination: {
            total: number;
        };
    };
}

export interface AddProjectDocumentRequest {
    project_id: string;
    document_name: string;
    document: File;
}

export interface ProjectDocumentResponse {
    success: boolean;
    status: number;
    message: string;
    data?: {
        document_id: number;
        project_id: string;
        document_name: string;
        document_url: string;
        created_at: string;
        updated_at: string;
    };
}

// Child Sponsorship Types
export interface Child {
    child_id: number;
    name: string;
    age: number;
    location: string;
    education_stage: string;
    bio: string;
    profile_image: string | null;
    is_verified: boolean;
    funding_options?: {
        weekly: number;
        monthly: number;
        yearly: number;
    };
    funding_requirements?: {
        weekly: number;
        monthly: number;
        yearly: number;
    };
    status?: 'available' | 'sponsored' | 'graduated';
    sponsor_id?: number | null;
    sponsor_name?: string | null;
    sponsor_email?: string | null;
    sponsor_phone?: string | null;
    total_raised?: number;
    target_amount?: number;
    sponsorship_start_date?: string | null;
    last_update_date?: string | null;
    created_at?: string;
    updated_at?: string;
    verification_notes?: string;
    verified_by?: string;
    verified_at?: string | null;
    sponsorships?: any[];
    updates_count?: number;
    is_active?: boolean;
}

export interface ChildListResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        children: Child[];
        pagination: {
            current_page: number;
            total_pages: number;
            total_children: number;
            limit: number;
        };
    };
}

export interface ChildResponse {
    success: boolean;
    status: number;
    message: string;
    data?: Child;
}

export interface CreateChildRequest {
    name: string;
    age: number;
    location: string;
    education_stage: string;
    bio: string;
    currency_code?: string;
    weekly_funding_required: number;
    monthly_funding_required: number;
    yearly_funding_required: number;
    photo?: File;
    verification_notes?: string;
}

export interface UpdateChildRequest {
    child_id: number;
    name: string;
    age: number;
    location: string;
    education_stage: string;
    bio: string;
    currency_code?: string;
    weekly_funding_required: number;
    monthly_funding_required: number;
    yearly_funding_required: number;
    photo?: File;
    verification_notes?: string;
    status?: string;
}

export interface DeleteChildRequest {
    child_id: number;
}

export interface VerifyChildRequest {
    child_id: number;
    verified: boolean;
    verification_notes?: string;
}

// Sponsorship Updates Types
export interface SponsorshipUpdate {
    update_id: number;
    child: {
        child_id: number;
        name: string;
        profile_image: string | null;
    };
    update_type: 'progress_report' | 'photo_update' | 'video_update' | 'general_update';
    title: string;
    description: string;
    content?: string;
    images: string[];
    videos: string[];
    update_date: string;
    created_by: string;
    is_notification_sent: boolean;
    created_at: string;
}

export interface SponsorshipUpdatesListResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        updates: SponsorshipUpdate[];
        pagination: {
            current_page: number;
            total_pages: number;
            total_updates: number;
            limit: number;
        };
    };
}

export interface SponsorshipUpdateResponse {
    success: boolean;
    status: number;
    message: string;
    data?: {
        update_id: number;
        child_id: number;
        child_name: string;
        update_type: string;
        title: string;
        description: string;
        content: string;
        images: string[];
        videos: string[];
        update_date: string;
        created_by: string;
        is_notification_sent: boolean;
        created_at: string;
        message: string;
    };
}

export interface CreateSponsorshipUpdateRequest {
    child_id: number;
    title: string;
    description: string;
    content?: string;
    update_type: 'progress_report' | 'photo_update' | 'video_update' | 'general_update';
    update_date: string;
    images?: File[];
    videos?: File[];
}

export interface UpdateSponsorshipUpdateRequest {
    update_id: number;
    child_id: number;
    title: string;
    description: string;
    content?: string;
    update_type: 'progress_report' | 'photo_update' | 'video_update' | 'general_update';
    update_date: string;
    images?: File[];
    videos?: File[];
}

export interface DeleteSponsorshipUpdateRequest {
    update_id: number;
}

// Child Sponsorship Statistics

// User-facing Child Sponsorship Types
export interface AvailableChild {
    child_id: number;
    name: string;
    age: number;
    location: string;
    education_stage: string;
    bio: string;
    profile_image: string | null;
    currency_code: string;
    currency_symbol: string;
    is_verified: boolean;
    funding_options: {
        weekly: number;
        monthly: number;
        yearly: number;
    };
}

export interface AvailableChildrenResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        children: AvailableChild[];
        pagination: {
            current_page: number;
            total_pages: number;
            total_children: number;
            limit: number;
        };
    };
}

export interface AvailableChildrenParams {
    page?: number;
    limit?: number;
    age_min?: number;
    age_max?: number;
    location?: string;
    education_stage?: string;
    filter_currency?: boolean;
}

export interface ChildDetailsResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        child_id: number;
        name: string;
        age: number;
        location: string;
        education_stage: string;
        bio: string;
        profile_image: string | null;
        currency_code: string;
        currency_symbol: string;
        funding_options: {
            weekly: number;
            monthly: number;
            yearly: number;
        };
        status: string;
        created_at: string;
    };
}

export interface SponsorChildRequest {
    child_id: number;
    sponsorship_type: 'weekly' | 'monthly' | 'yearly' | 'one_time';
    amount: number;
    auto_renewal?: boolean;
    notes?: string;
}

export interface SponsorChildResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        sponsorship_id: number;
        child_name: string;
        amount: number;
        sponsorship_type: string;
        wallet_balance: number;
        message: string;
    };
}

export interface SponsoredChild {
    sponsorship_id: number;
    child: {
        child_id: number;
        name: string;
        age: number;
        location: string;
        education_stage: string;
        profile_image: string | null;
    };
    sponsorship_type: 'weekly' | 'monthly' | 'yearly' | 'one_time';
    amount: number;
    status: 'active' | 'paused' | 'cancelled' | 'completed';
    start_date: string;
    end_date: string | null;
    next_payment_date: string | null;
    auto_renewal: boolean;
    notes: string | null;
    created_at: string;
}

export interface SponsoredChildrenResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        sponsorships: SponsoredChild[];
        pagination: {
            current_page: number;
            total_pages: number;
            total_sponsorships: number;
            limit: number;
        };
    };
}

export interface SponsoredChildrenParams {
    page?: number;
    limit?: number;
    status?: 'active' | 'paused' | 'cancelled' | 'completed' | 'all';
}

export interface SponsorshipUpdate {
    update_id: number;
    update_type: 'progress_report' | 'photo_update' | 'video_update' | 'general_update';
    title: string;
    description: string;
    content?: string;
    images: string[];
    videos: string[];
    update_date: string;
    created_by: string;
    created_at: string;
}

export interface SponsorshipUpdatesResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        updates: SponsorshipUpdate[];
        pagination: {
            current_page: number;
            total_pages: number;
            total_updates: number;
            limit: number;
        };
    };
}

export interface SponsorshipUpdatesParams {
    page?: number;
    limit?: number;
    update_type?: 'progress_report' | 'photo_update' | 'video_update' | 'general_update';
}

export interface UpdateSponsorshipRequest {
    sponsorship_id: number;
    auto_renewal?: boolean;
    status?: 'active' | 'paused' | 'cancelled' | 'completed';
    cancellation_reason?: string;
    pause_reason?: string;
}

export interface UpdateSponsorshipResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        sponsorship_id: number;
        child_name: string;
        status: string;
        auto_renewal: boolean;
        next_payment_date: string | null;
        paused_at: string | null;
        message: string;
    };
}

export interface UserSponsorshipStats {
    total_sponsorships: number;
    active_sponsorships: number;
    total_amount_sponsored: number;
    sponsorships_by_type: Array<{
        type: string;
        count: number;
        total_amount: number;
    }>;
}

export interface UserSponsorshipStatsResponse {
    success: boolean;
    status: number;
    message: string;
    data: UserSponsorshipStats;
}

export interface SupportedCurrency {
    code: string;
    symbol: string;
    name: string;
}

export interface SupportedCurrenciesResponse {
    success: boolean;
    status: number;
    message: string;
    data: SupportedCurrency[];
}

// Contact Interfaces
export interface ContactSubmission {
    id: number;
    name: string;
    email: string;
    subject: string;
    message: string;
    status: 'pending' | 'replied' | 'resolved';
    created_at: string;
    updated_at: string;
}

export interface ContactListResponse {
    success: boolean;
    status: number;
    message: string;
    data: ContactSubmission[];
}

export interface ContactResponse {
    success: boolean;
    status: number;
    message: string;
    data?: ContactSubmission;
}

export interface CreateContactRequest {
    name: string;
    email: string;
    subject: string;
    message: string;
}

export interface UpdateContactRequest {
    id: number;
    status: 'pending' | 'replied' | 'resolved';
}

export interface ContactStatistics {
    total_submissions: number;
    pending_submissions: number;
    read_submissions: number;
    replied_submissions: number;
    resolved_submissions: number;
}

export interface ContactStatisticsResponse {
    success: boolean;
    status: number;
    message: string;
    data: ContactStatistics;
}

// Solar Lead Types
export interface SolarLead {
    lead_id: number;
    name: string;
    email: string;
    phone: string;
    country: string;
    state: string;
    local_government_area: string;
    property_type: string | null;
    message?: string | null;
    status: 'new' | 'contacted' | 'meeting_scheduled' | 'converted' | 'not_interested';
    meeting_date?: string | null;
    meeting_type?: 'call' | 'video' | 'in_person' | null;
    meeting_notes?: string | null;
    notes?: string | null;
    ip_address?: string;
    user_agent?: string;
    created_at: string;
    updated_at: string;
    assigned_to_admin?: any | null;
}

export interface CreateSolarLeadRequest {
    name: string;
    email: string;
    phone: string;
    country: string;
    state: string;
    local_government_area: string;
    property_type?: 'residential' | 'commercial' | 'industrial' | 'agricultural' | 'other' | null;
    message?: string | null;
}

export interface UpdateSolarLeadStatusRequest {
    lead_id: number;
    status: 'new' | 'contacted' | 'meeting_scheduled' | 'converted' | 'not_interested';
    notes?: string | null;
}

export interface ScheduleSolarMeetingRequest {
    lead_id: number;
    meeting_type: 'call' | 'video' | 'in_person';
    meeting_date: string;
    meeting_notes?: string | null;
}

export interface UpdateSolarMeetingRequest {
    lead_id: number;
    meeting_type?: 'call' | 'video' | 'in_person';
    meeting_date?: string;
    meeting_notes?: string | null;
}

export interface UpdateSolarLeadNotesRequest {
    lead_id: number;
    notes: string;
}

export interface SolarLeadsResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        leads: SolarLead[];
        pagination: {
            page: number;
            totalPages: number;
            total: number;
            limit: number;
        };
        statistics?: {
            total: number;
            new: number;
            contacted: number;
            meeting_scheduled: number;
            converted: number;
            not_interested: number;
        };
    };
}

export interface SolarLeadResponse {
    success: boolean;
    status: number;
    message: string;
    data: SolarLead;
}

// Membership Plan Types
export interface MembershipPlan {
    plan_id: number;
    name: string;
    description: string | null;
    price: string | number;
    duration_days: number;
    payment_type: 'one_time' | 'monthly_installment';
    monthly_installment_amount: string | number | null;
    monthly_installment_count: number | null;
    access_level: 'silver' | 'gold' | 'platinum' | null;
    is_active: boolean;
    max_bookings_per_month: number;
    created_at: string;
    updated_at: string;
    memberships?: any[];
}

export interface MembershipPlanListResponse {
    success: boolean;
    status: number;
    message: string;
    data: MembershipPlan[];
}

export interface MembershipPlanResponse {
    success: boolean;
    status: number;
    message: string;
    data: MembershipPlan;
}

export interface CreateMembershipPlanRequest {
    name: string;
    description?: string | null;
    price: number;
    duration_days: number;
    payment_type: 'one_time' | 'monthly_installment';
    monthly_installment_amount?: number | null;
    monthly_installment_count?: number | null;
    access_level?: 'silver' | 'gold' | 'platinum' | null;
    max_bookings_per_month?: number;
}

export interface UpdateMembershipPlanRequest {
    plan_id: number;
    name?: string;
    description?: string | null;
    price?: number;
    duration_days?: number;
    payment_type?: 'one_time' | 'monthly_installment';
    monthly_installment_amount?: number | null;
    monthly_installment_count?: number | null;
    access_level?: 'silver' | 'gold' | 'platinum' | null;
    is_active?: boolean;
    max_bookings_per_month?: number;
}

export interface DeleteMembershipPlanRequest {
    plan_id: number;
}

// Apartment Management Types
export interface Apartment {
    apartment_id: number;
    name: string;
    description: string | null;
    location: string;
    address: string | null;
    capacity: number;
    amenities: string | null;
    images: string[];
    is_active: boolean;
    is_available: boolean;
    created_at: string;
    updated_at: string;
    bookings?: any[];
    bookings_count?: number;
    availabilities?: ApartmentAvailability[];
}

export interface ApartmentAvailability {
    availability_id: number;
    date: string;
    is_available: boolean;
    reason: string | null;
    created_at: string;
    updated_at: string;
}

export interface ApartmentListResponse {
    success: boolean;
    status: number;
    message: string;
    data: Apartment[];
}

export interface ApartmentResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        apartment: Apartment;
        availability: ApartmentAvailability[];
    };
}

export interface CreateApartmentRequest {
    name: string;
    description?: string | null;
    location: string;
    address?: string | null;
    capacity: number;
    amenities?: string | null;
    images?: File[] | string[];
}

export interface UpdateApartmentRequest {
    apartment_id: number;
    name?: string;
    description?: string | null;
    location?: string;
    address?: string | null;
    capacity?: number;
    amenities?: string | null;
    is_active?: boolean;
    is_available?: boolean;
    images?: File[] | string[];
}

export interface ManageApartmentAvailabilityRequest {
    apartment_id: number;
    dates: string[];
    is_available: boolean;
    reason?: string | null;
}

export interface ManageApartmentAvailabilityResponse {
    success: boolean;
    status: number;
    message: string;
    data: Array<{
        date: string;
        is_available: boolean;
        reason: string | null;
    }>;
}

// Booking Management Types
export interface Booking {
    booking_id: number;
    booking_reference: string;
    user: {
        user_id: number;
        name: string;
        email: string;
        mobile_number?: string;
    };
    apartment: {
        apartment_id: number;
        name: string;
        location: string;
        address?: string;
        image: string | null;
        images?: string[];
    };
    membership?: {
        membership_id: number;
        status: string;
    } | null;
    check_in_date: string;
    check_out_date: string;
    guests: number;
    status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
    cancellation_reason?: string | null;
    cancelled_at?: string | null;
    is_refunded: boolean;
    refund_amount?: number | null;
    total_amount?: number | null;
    amount?: number | null;
    created_at: string;
    updated_at?: string;
}

export interface BookingListResponse {
    success: boolean;
    status: number;
    message: string;
    data: Booking[];
}

export interface BookingResponse {
    success: boolean;
    status: number;
    message: string;
    data: Booking;
}

export interface BookingListParams {
    page?: number;
    limit?: number;
    status?: 'pending' | 'confirmed' | 'cancelled' | 'completed' | 'all';
    user_id?: number;
    start_date?: string;
    end_date?: string;
}

export interface UpdateBookingStatusRequest {
    booking_id: number;
    status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
    cancellation_reason?: string | null;
}

export interface ProcessRefundRequest {
    booking_id: number;
    refund_amount: number;
    notes?: string | null;
}

export interface ProcessRefundResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        booking_id: number;
        refund_amount: number;
        wallet_balance: number;
    };
}

// Membership Management Types (User Memberships, not Plans)
export interface UserMembership {
    membership_id: number;
    user: {
        user_id: number;
        name: string;
        email: string;
    };
    plan: {
        plan_id: number;
        name: string;
        price: number;
    };
    status: 'active' | 'expired' | 'cancelled';
    start_date: string;
    end_date: string;
    payment_type: string;
    installments_paid?: number | null;
    total_installments?: number | null;
    next_installment_date?: string | null;
    created_at: string;
}

export interface MembershipListResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        memberships: UserMembership[];
        pagination: {
            current_page: number;
            total_pages: number;
            total_memberships: number;
            limit: number;
        };
    };
}

export interface MembershipListParams {
    page?: number;
    limit?: number;
    status?: 'active' | 'expired' | 'cancelled' | 'all';
    user_id?: number;
}

export interface UpdateMembershipStatusRequest {
    membership_id: number;
    status: 'active' | 'expired' | 'cancelled';
}

export interface MembershipResponse {
    success: boolean;
    status: number;
    message: string;
    data: UserMembership;
}

// Marketplace Types
export interface MarketplaceListing {
    listing_id: number;
    listing_type: 'car' | 'house';
    title: string;
    description: string;
    price: number;
    currency_code: string | null;
    currency_symbol: string | null;
    location: string | null;
    tags: string[] | null;
    car_details: any;
    house_details: any;
    status: 'draft' | 'active' | 'inactive' | 'sold';
    is_featured: boolean;
    created_at: string;
    updated_at: string;
    images: Array<{
        image_id: number;
        image_path: string;
        is_primary: boolean;
    }>;
    listing_source?: 'admin' | 'dealer';
    dealer?: {
        dealer_id: number;
        name: string;
        email?: string;
        business_name: string;
    } | null;
    approval_status?: 'pending' | 'approved' | 'rejected';
    rejection_reason?: string | null;
}

export interface MarketplaceListingsResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        listings: MarketplaceListing[];
        pagination: {
            current_page: number;
            total_pages: number;
            total_records: number;
        };
    };
}

export interface MarketplaceListingResponse {
    success: boolean;
    status: number;
    message: string;
    data: MarketplaceListing;
}

export interface MarketplaceListingParams {
    page?: number;
    limit?: number;
    status?: 'draft' | 'active' | 'inactive' | 'sold' | 'all';
    listing_type?: 'car' | 'house' | 'all';
    search?: string;
}

export interface SoldListingsParams {
    page?: number;
    limit?: number;
    listing_type?: 'car' | 'house' | 'all';
    listing_source?: 'admin' | 'dealer' | 'all';
}

export interface PendingListingsParams {
    page?: number;
    limit?: number;
    listing_type?: 'car' | 'house' | 'all';
}

export interface ApproveListingRequest {
    listing_id: number;
    admin_notes?: string;
}

export interface RejectListingRequest {
    listing_id: number;
    rejection_reason: string;
}

export interface CreateMarketplaceListingRequest {
    listing_type: 'car' | 'house';
    title: string;
    description: string;
    price: number;
    currency_code?: string;
    currency_symbol?: string;
    location?: string;
    tags?: string[];
    car_details?: any;
    house_details?: any;
    status?: 'draft' | 'active' | 'inactive' | 'sold';
    is_featured?: boolean;
    images?: File[];
}

export interface UpdateMarketplaceListingRequest {
    listing_id: number;
    listing_type?: 'car' | 'house';
    title?: string;
    description?: string;
    price?: number;
    currency_code?: string;
    currency_symbol?: string;
    location?: string;
    tags?: string[];
    car_details?: any;
    house_details?: any;
    status?: 'draft' | 'active' | 'inactive' | 'sold';
    is_featured?: boolean;
    remove_image_ids?: number[];
    images?: File[];
}

export interface UpdateMarketplaceListingStatusRequest {
    listing_id: number;
    status: 'draft' | 'active' | 'inactive' | 'sold';
}

export interface MarketplaceInquiry {
    inquiry_id: number;
    message: string;
    status: 'new' | 'in_progress' | 'resolved';
    admin_response: string | null;
    contact_email: string;
    contact_phone: string | null;
    listing: {
        listing_id: number;
        title: string;
        listing_type: 'car' | 'house';
    } | null;
    user: {
        user_id: number;
        name: string;
        email: string;
    } | null;
    created_at: string;
}

export interface MarketplaceInquiriesResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        inquiries: MarketplaceInquiry[];
        pagination: {
            current_page: number;
            total_pages: number;
            total_records: number;
        };
    };
}

export interface MarketplaceInquiriesParams {
    page?: number;
    limit?: number;
    status?: 'new' | 'in_progress' | 'resolved' | 'all';
}

export interface RespondMarketplaceInquiryRequest {
    inquiry_id: number;
    status: 'new' | 'in_progress' | 'resolved';
    admin_response?: string;
}

export interface MarketplaceTransaction {
    transaction_id: number;
    amount: number;
    payment_method: string;
    status: 'processing' | 'completed' | 'failed' | 'cancelled' | 'refunded';
    wallet_amount: number;
    is_large_transaction: boolean;
    is_below_min_threshold: boolean;
    payment_screenshot: string | null;
    external_reference: string | null;
    admin_notes: string | null;
    listing: {
        listing_id: number;
        title: string;
    } | null;
    user: {
        user_id: number;
        name: string;
        email: string;
    } | null;
    verified_by: string | null;
    verified_at: string | null;
    created_at: string;
}

export interface MarketplaceTransactionsResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        transactions: MarketplaceTransaction[];
        pagination: {
            current_page: number;
            total_pages: number;
            total_records: number;
        };
    };
}

export interface MarketplaceTransactionsParams {
    page?: number;
    limit?: number;
    status?: 'processing' | 'completed' | 'failed' | 'cancelled' | 'refunded' | 'all';
    payment_method?: string;
}

export interface UpdateMarketplaceTransactionStatusRequest {
    transaction_id: number;
    status: 'processing' | 'completed' | 'failed' | 'cancelled' | 'refunded';
    admin_notes?: string;
    mark_listing_sold?: boolean;
}

export interface MarketplaceSettings {
    settings_id: number;
    min_transaction_amount: string;
    max_transaction_amount: string;
    large_transaction_flag_amount: string;
    post_kyc_transaction_limit: string;
    wallet_without_kyc_limit: string;
    wallet_daily_cap: string;
    allow_wallet_payments: boolean;
    allow_bank_transfer: boolean;
    allow_online_gateway: boolean;
    created_at: string;
    updated_at: string;
}

export interface MarketplaceSettingsResponse {
    success: boolean;
    status: number;
    message: string;
    data: MarketplaceSettings;
}

export interface UpdateMarketplaceSettingsRequest {
    min_transaction_amount?: number;
    max_transaction_amount?: number;
    large_transaction_flag_amount?: number;
    post_kyc_transaction_limit?: number;
    wallet_without_kyc_limit?: number;
    wallet_daily_cap?: number;
    allow_wallet_payments?: boolean;
    allow_bank_transfer?: boolean;
    allow_online_gateway?: boolean;
}

export interface MarketplaceKycRequest {
    kyc_request_id: number;
    status: 'pending' | 'approved' | 'rejected';
    document_type: string;
    document_front: string;
    document_back: string;
    address_proof: string;
    selfie_image: string;
    admin_comments: string | null;
    user: {
        user_id: number;
        name: string;
        email: string;
        marketplace_kyc_status: string;
    };
    reviewed_by: string | null;
    reviewed_at: string | null;
    created_at: string;
}

export interface MarketplaceKycRequestsResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        requests: MarketplaceKycRequest[];
        pagination: {
            current_page: number;
            total_pages: number;
            total_records: number;
        };
    };
}

export interface MarketplaceKycRequestsParams {
    page?: number;
    limit?: number;
    status?: 'pending' | 'approved' | 'rejected' | 'all';
}

export interface UpdateMarketplaceKycStatusRequest {
    kyc_request_id: number;
    status: 'approved' | 'rejected';
    admin_comments?: string;
}

// User-facing Marketplace Types
export interface UserMarketplaceListingParams {
    page?: number;
    limit?: number;
    listing_type?: 'car' | 'house';
    min_price?: number;
    max_price?: number;
    search?: string;
    location?: string;
    featured?: boolean;
    listing_source?: 'admin' | 'dealer';
    manufacturer?: string;
    model?: string;
}

export interface CreateMarketplaceInquiryRequest {
    listing_id: number;
    message: string;
    contact_email?: string;
    contact_phone?: string;
}

export interface UserMarketplaceInquiriesParams {
    page?: number;
    limit?: number;
    status?: 'new' | 'in_progress' | 'resolved';
}

export interface MarketplacePaymentOptionsResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        wallet_balance: number;
        currency_symbol: string;
        kyc_status: string;
        settings: MarketplaceSettings;
        bank_accounts: Array<{
            bank_account_id: number;
            account_holder_name: string;
            account_number: string;
            bank_name: string;
            country_code: string;
            created_at: string;
            updated_at: string;
        }>;
    };
}

export interface PurchaseMarketplaceListingRequest {
    listing_id: number;
    amount: number;
    payment_method: 'wallet' | 'bank_transfer' | 'online_gateway';
    wallet_amount?: number;
    transaction_reference?: string;
    notes?: string;
    payment_screenshot?: File;
}

export interface PurchaseMarketplaceListingResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        transaction_id: number;
        status: string;
        listing_id: number;
        wallet_balance: number;
        is_large_transaction: boolean;
    };
}

export interface UploadPaymentProofRequest {
    transaction_id: number;
    payment_screenshot: File;
}

export interface MarketplaceKycStatusResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        status: string;
        rejection_reason: string | null;
        transaction_limit: number;
        latest_request: {
            kyc_request_id: number;
            status: string;
            document_type: string;
            submitted_at: string;
            admin_comments: string | null;
        } | null;
    };
}

export interface SubmitMarketplaceKycRequest {
    document_type: string;
    document_front: File;
    document_back?: File;
    address_proof: File;
    selfie_image?: File;
    admin_comments?: string;
}

// User-facing Membership & Booking Types
export interface PurchaseMembershipRequest {
    plan_id: number;
    payment_type: 'one_time' | 'monthly_installment';
}

export interface PurchaseMembershipResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        membership_id: number;
        plan_name: string;
        status: string;
        end_date: string;
    };
}

export interface UserMembershipStatusResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        has_membership: boolean;
        membership: UserMembership | null;
        is_active: boolean;
        days_remaining: number;
    };
}

export interface CreateBookingRequest {
    apartment_id: number;
    check_in_date: string;
    check_out_date: string;
    guests: number;
}

export interface CreateBookingResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        booking_id: number;
        booking_reference: string;
        status: string;
    };
}

export interface CancelBookingRequest {
    booking_id: number;
    reason?: string;
}

export interface UserApartmentListParams {
    page?: number;
    limit?: number;
    location?: string;
    capacity?: number;
    search?: string;
}

export interface UserBookingListParams {
    page?: number;
    limit?: number;
    status?: 'pending' | 'confirmed' | 'cancelled' | 'completed' | 'all';
}

// Dealer Management Types
export interface Dealer {
    dealer_id: number;
    name: string;
    email: string;
    mobile_number: string | null;
    profile_image: string | null;
    business_name: string;
    business_address: string | null;
    business_phone: string | null;
    dealer_type: 'car' | 'house';
    verification_status: 'pending' | 'approved' | 'rejected';
    is_active: boolean;
    is_deleted?: boolean;
    deleted_at?: string | null;
    rejection_reason: string | null;
    country_code: string | null;
    currency_symbol: string | null;
    verified_by: {
        admin_id: number;
        name: string;
        email: string;
    } | null;
    verified_at: string | null;
    created_at: string;
    updated_at: string | null;
}

export interface DealerDocument {
    document_id: number;
    document_type: string;
    document_path: string;
    document_name: string;
    created_at: string;
}

export interface DealersResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        dealers: Dealer[];
        pagination: {
            current_page: number;
            total_pages: number;
            total_records: number;
        };
    };
}

export interface PendingDealersResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        dealers: Array<Dealer & { documents: DealerDocument[] }>;
        pagination: {
            current_page: number;
            total_pages: number;
            total_records: number;
        };
    };
}

export interface DealerDetailsResponse {
    success: boolean;
    status: number;
    message: string;
    data: Dealer & {
        documents: DealerDocument[];
        statistics: {
            total_listings: number;
            active_listings: number;
            sold_listings: number;
        };
    };
}

export interface DealerDocumentsResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        documents: DealerDocument[];
    };
}

export interface DealerStatsResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        total_dealers: number;
        pending_dealers: number;
        approved_dealers: number;
        rejected_dealers: number;
        car_dealers: number;
        house_dealers: number;
        total_dealer_listings: number;
        active_dealer_listings: number;
        sold_dealer_listings: number;
    };
}

export interface DealersParams {
    page?: number;
    limit?: number;
    verification_status?: 'pending' | 'approved' | 'rejected' | 'all';
    dealer_type?: 'car' | 'house' | 'all';
    search?: string;
    deleted_status?: 'active' | 'deleted' | 'all';
}

export interface PendingDealersParams {
    page?: number;
    limit?: number;
}

export interface VerifyDealerRequest {
    dealer_id: number;
    status: 'approved' | 'rejected';
    admin_notes?: string;
}

export interface UpdateDealerStatusRequest {
    dealer_id: number;
    is_active: boolean;
}

// Dealer Auth Types
export interface DealerProfileResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        dealer_id: number;
        name: string;
        email: string;
        mobile_number: string | null;
        profile_image: string | null;
        business_name: string;
        business_address: string | null;
        business_phone: string | null;
        dealer_type: 'car' | 'house';
        verification_status: 'pending' | 'approved' | 'rejected';
        rejection_reason: string | null;
        country_code: string | null;
        currency_symbol: string | null;
        documents: Array<{
            document_id: number;
            document_type: string;
            document_path: string;
            document_name: string;
        }>;
    };
}

export interface RegisterDealerRequest {
    name: string;
    email: string;
    mobile_number?: string;
    password?: string;
    business_name: string;
    business_address?: string;
    business_phone?: string;
    dealer_type: 'car' | 'house';
    country_code?: string;
    currency_symbol?: string;
    business_license?: File;
    id_proof?: File;
    address_proof?: File;
    tax_certificate?: File;
    other?: File[];
}

export interface UpdateDealerProfileRequest {
    name?: string;
    mobile_number?: string | null;
    business_name?: string;
    business_address?: string | null;
    business_phone?: string | null;
    country_code?: string | null;
    currency_symbol?: string | null;
    file?: File;
}

export interface UpdateDealerDocumentsRequest {
    business_license?: File;
    id_proof?: File;
    address_proof?: File;
    tax_certificate?: File;
    other?: File[];
}

export interface DealerLoginResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        token: string;
        dealer: {
            dealer_id: number;
            name: string;
            email: string;
            business_name: string;
            dealer_type: 'car' | 'house';
            verification_status: 'pending' | 'approved' | 'rejected';
            rejection_reason: string | null;
            is_profile_complete: boolean;
        };
    };
}

export interface DealerDashboardStatsResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        total_listings: number;
        active_listings: number;
        pending_listings: number;
        sold_listings: number;
        rejected_listings: number;
    };
}

// Dealer Listing Types
export interface DealerListing {
    listing_id: number;
    listing_type: 'car' | 'house';
    title: string;
    description: string;
    price: number;
    currency_code: string | null;
    currency_symbol: string | null;
    location: string | null;
    status: 'draft' | 'active' | 'inactive' | 'sold';
    approval_status: 'pending' | 'approved' | 'rejected';
    rejection_reason: string | null;
    images: Array<{
        image_id: number;
        image_path: string;
        is_primary: boolean;
    }>;
    created_at: string;
    updated_at?: string;
}

export interface DealerListingDetail extends DealerListing {
    tags: string[] | null;
    car_details: any;
    house_details: any;
}

export interface DealerListingsResponse {
    success: boolean;
    status: number;
    message: string;
    data: {
        listings: DealerListing[];
        pagination: {
            current_page: number;
            total_pages: number;
            total_records: number;
        };
    };
}

export interface DealerListingResponse {
    success: boolean;
    status: number;
    message: string;
    data: DealerListingDetail;
}

export interface DealerListingsParams {
    page?: number;
    limit?: number;
    status?: 'draft' | 'active' | 'inactive' | 'sold' | 'all';
    approval_status?: 'pending' | 'approved' | 'rejected' | 'all';
}

export interface CreateDealerListingRequest {
    listing_type: 'car' | 'house';
    title: string;
    description: string;
    price: number;
    currency_code?: string;
    currency_symbol?: string;
    location?: string;
    tags?: string[];
    car_details?: {
        vin: string;
        make?: string;
        model?: string;
        year?: number;
        mileage?: number;
    };
    house_details?: any;
    images?: File[];
}

export interface UpdateDealerListingRequest {
    listing_id: number;
    title?: string;
    description?: string;
    price?: number;
    currency_code?: string;
    currency_symbol?: string;
    location?: string;
    tags?: string[];
    car_details?: {
        vin?: string;
        make?: string;
        model?: string;
        year?: number;
        mileage?: number;
    };
    house_details?: any;
    remove_image_ids?: number[];
    images?: File[];
}

// Admin API slice
import { API_BASE_URL } from '../../config/api';

export const adminApi = createApi({
    reducerPath: 'adminApi',
    // Add response interceptor to handle 401 errors
    baseQuery: async (args, api, extraOptions) => {
        const result = await fetchBaseQuery({
            baseUrl: API_BASE_URL,
            prepareHeaders: (headers, { endpoint }) => {
                // Add auth token if available
                const token = localStorage.getItem('diasporawise_admin_token');

                if (token) {
                    headers.set('Authorization', `Bearer ${token}`);
                    console.log('Sending request with token:', token.substring(0, 20) + '...');
                } else {
                    console.warn('No authentication token found');
                }

                // Only set Content-Type for non-FormData requests
                // Don't set Content-Type for FormData requests - browser sets it automatically with boundary
                if (!endpoint.includes('addProjectImages') &&
                    !endpoint.includes('addProjectDocuments') &&
                    !endpoint.includes('getBlogs') &&
                    !endpoint.includes('createBlog') &&
                    !endpoint.includes('updateBlog') &&
                    !endpoint.includes('createCTAPopup') &&
                    !endpoint.includes('updateCTAPopup') &&
                    !endpoint.includes('createChild') &&
                    !endpoint.includes('updateChild') &&
                    !endpoint.includes('createSponsorshipUpdate') &&
                    !endpoint.includes('updateSponsorshipUpdate') &&
                    !endpoint.includes('updateProfile') &&
                    !endpoint.includes('updateSuperAdminProfile') &&
                    !endpoint.includes('account-details-update') &&
                    !endpoint.includes('createApartment') &&
                    !endpoint.includes('updateApartment') &&
                    !endpoint.includes('createMarketplaceListing') &&
                    !endpoint.includes('updateMarketplaceListing') &&
                    !endpoint.includes('purchaseMarketplaceListing') &&
                    !endpoint.includes('uploadMarketplacePaymentProof') &&
                    !endpoint.includes('submitMarketplaceKyc') &&
                    !endpoint.includes('purchaseMembership') &&
                    !endpoint.includes('createInvestment') &&
                    !endpoint.includes('addFundsToWallet') &&
                    !endpoint.includes('registerDealer') &&
                    !endpoint.includes('updateDealerProfile') &&
                    !endpoint.includes('updateDealerDocuments') &&
                    !endpoint.includes('createDealerListing') &&
                    !endpoint.includes('updateDealerListing') &&
                    !endpoint.includes('applyForJob')) {
                    headers.set('Content-Type', 'application/json');
                }

                return headers;
            },
        })(args, api, extraOptions);

        // Function to handle logout and redirect
        const handleAuthError = () => {
            console.error('Authentication failed - token may be expired');
            // Clear invalid token
            localStorage.removeItem('diasporawise_admin_token');
            localStorage.removeItem('diasporawise_admin_user');

            if (typeof window !== 'undefined') {
                alert('Your session has expired. Please login again.');
                window.location.href = '/admin/login';
            }
        };

        // Check if response has success: false and treat it as an error
        if (result.data && typeof result.data === 'object' && 'success' in result.data && result.data.success === false) {
            const errorData = result.data as any;
            const errorStatus = errorData.status || 400;
            const errorMessage = errorData.message || 'Request failed';
            
            console.error('API Response with success: false:', {
                endpoint: args?.url || 'unknown',
                method: args?.method || 'unknown',
                status: errorStatus,
                message: errorMessage,
                data: errorData
            });

            // Handle 401 from data body
            if (errorStatus === 401) {
                handleAuthError();
            }

            // Return as error so it can be caught in catch blocks
            return {
                error: {
                    status: errorStatus,
                    data: errorData,
                    message: errorMessage
                }
            };
        }

        // Handle 401 from result.error
        if (result.error && result.error.status === 401) {
            handleAuthError();
        }

        // Log all API errors for debugging
        if (result.error) {
            console.error('API Error:', {
                endpoint: args?.url || 'unknown',
                method: args?.method || 'unknown',
                status: result.error.status,
                data: result.error.data,
                message: (result.error.data as any)?.message || (result.error as any).message
            });
        }

        return result;
    },
    tagTypes: ['AdminDashboard', 'AdminUsers', 'AdminCategories', 'AdminBankAccounts', 'AdminInvestments', 'AdminProjects', 'Profile', 'Notifications', 'UserDetails', 'CTAPopups', 'ContactSubmissions', 'ContactStatistics', 'Blogs', 'Testimonials', 'PublicTestimonials', 'LocationSearch', 'LatLong', 'ProjectDetails', 'PublicBlogs', 'FeaturedBlogs', 'BlogDetail', 'PublicProjects', 'Investment', 'Project', 'WalletTransactions', 'WalletStats', 'ChildSponsorship', 'ChildSponsorshipUpdates', 'ChildSponsorshipStats', 'MembershipPlans', 'Memberships', 'Apartments', 'Bookings', 'MarketplaceListings', 'MarketplaceInquiries', 'MarketplaceTransactions', 'MarketplaceSettings', 'MarketplaceKycRequests', 'Dealers', 'DealerProfile', 'DealerDashboard', 'DealerListings', 'SolarLeads', 'Jobs', 'JobApplications'],
    endpoints: (builder) => ({
        // Admin login
        adminLogin: builder.mutation<{
            success: boolean;
            status: number;
            message: string;
            data: {
                token: string;
                user: {
                    email: string;
                    role: 'admin' | 'manager' | 'exclusive';
                    name: string;
                };
            };
        }, { email: string; password: string }>({
            query: ({ email, password }) => ({
                url: '/admin/login',
                method: 'POST',
                body: { email, password },
            }),
        }),

        // Get admin dashboard details
        getAdminDashboardDetails: builder.query<DashboardDetails, void>({
            query: () => '/admin/dashboard-details',
            providesTags: ['AdminDashboard'],
        }),

        // Get user list
        getAdminUserList: builder.query<UserListResponse, { page?: number; limit?: number; search?: string }>({
            query: (params) => ({
                url: '/admin/user-list',
                params: {
                    page: params.page || 1,
                    limit: params.limit || 10,
                    ...(params.search && { search: params.search }),
                },
            }),
            providesTags: ['AdminUsers'],
        }),

        // Update user status (activate/deactivate)
        updateUserStatus: builder.mutation<UpdateUserStatusResponse, UpdateUserStatusRequest>({
            query: (body) => ({
                url: '/admin/change-user-status',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['AdminUsers', 'AdminDashboard'],
        }),

        // Restore soft-deleted user
        restoreUser: builder.mutation<RestoreUserResponse, RestoreUserRequest>({
            query: (body) => ({
                url: '/admin/restore-user',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['AdminUsers', 'AdminDashboard'],
        }),

        // Bulk delete users (soft or hard)
        bulkDeleteUsers: builder.mutation<{ success: boolean; message: string }, { user_ids: number[]; delete_type: 'soft' | 'hard' }>({
            query: (body) => ({
                url: '/admin/bulk-delete-users',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['AdminUsers', 'AdminDashboard'],
        }),


        // Get single user details
        getAdminUser: builder.query<User, string>({
            query: (userId) => `/admin/user-list/${userId}`,
            providesTags: ['AdminUsers'],
        }),

        // Delete user
        deleteAdminUser: builder.mutation<{ success: boolean; message: string }, string>({
            query: (userId) => ({
                url: `/admin/user-list/${userId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['AdminUsers', 'AdminDashboard'],
        }),

        // Category CRUD operations
        getAllCategories: builder.query<CategoryListResponse, void>({
            query: () => '/admin/get-all-category',
            providesTags: ['AdminCategories'],
        }),

        createCategory: builder.mutation<CategoryResponse, CreateCategoryRequest>({
            query: (body) => ({
                url: '/admin/create-category',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['AdminCategories'],
        }),

        updateCategory: builder.mutation<CategoryResponse, UpdateCategoryRequest>({
            query: (body) => ({
                url: '/admin/update-category',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['AdminCategories'],
        }),

        deleteCategory: builder.mutation<CategoryResponse, DeleteCategoryRequest>({
            query: (body) => ({
                url: '/admin/delete-category',
                method: 'DELETE',
                body,
            }),
            invalidatesTags: ['AdminCategories'],
        }),

        // Bank Account CRUD operations
        getAllBankAccounts: builder.query<BankAccountListResponse, void>({
            query: () => '/admin/get-all-bank-accounts',
            providesTags: ['AdminBankAccounts'],
        }),

        createBankAccount: builder.mutation<BankAccountResponse, CreateBankAccountRequest>({
            query: (body) => ({
                url: '/admin/add-bank-account',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['AdminBankAccounts'],
        }),

        updateBankAccount: builder.mutation<BankAccountResponse, UpdateBankAccountRequest>({
            query: (body) => ({
                url: '/admin/update-bank-account',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['AdminBankAccounts'],
        }),

        deleteBankAccount: builder.mutation<BankAccountResponse, DeleteBankAccountRequest>({
            query: (body) => ({
                url: '/admin/delete-bank-account',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['AdminBankAccounts'],
        }),

        // Admin Management CRUD operations
        getAllAdmins: builder.query<AdminListResponse, void>({
            query: () => '/admin/get-all-admins',
            providesTags: ['AdminUsers'],
        }),

        createAdmin: builder.mutation<AdminResponse, CreateAdminRequest>({
            query: (body) => ({
                url: '/admin/roles/create',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['AdminUsers'],
        }),

        updateAdmin: builder.mutation<AdminResponse, UpdateAdminRequest>({
            query: (body) => ({
                url: `/admin/roles/admin/${body.admin_id}`,
                method: 'PUT',
                body: {
                    name: body.name,
                    mobile_number: body.mobile_number,
                    password: body.password,
                    role: body.role
                },
            }),
            invalidatesTags: ['AdminUsers'],
        }),

        deleteAdmin: builder.mutation<AdminResponse, DeleteAdminRequest>({
            query: (body) => ({
                url: `/admin/roles/admin/${body.admin_id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['AdminUsers'],
        }),

        getRoleConfig: builder.query<{ success: boolean; status: number; message: string; data: Array<{ module: string; permissions: Array<{ key: string; label: string }> }> }, void>({
            query: () => '/admin/roles/config',
        }),

        getRoleDefaults: builder.query<{ success: boolean; status: number; message: string; data: Record<string, boolean> }, string>({
            query: (role) => `/admin/roles/defaults?role=${role}`,
        }),

        updateAdminRbac: builder.mutation<{ success: boolean; status: number; message: string; data: any }, { admin_id: number; role: string; permissions: Record<string, boolean> }>({
            query: (body) => ({
                url: '/admin/roles/update',
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['AdminUsers'],
        }),

        updateAdminStatus: builder.mutation<{ success: boolean; status: number; message: string }, { admin_id: number; is_active: boolean }>({
            query: (body) => ({
                url: '/admin/roles/update-status',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['AdminUsers'],
        }),

        // Investment Management
        getAllInvestments: builder.query<InvestmentListResponse, void>({
            query: () => '/admin/get-all-investments',
            providesTags: ['AdminInvestments'],
        }),

        approveRejectInvestment: builder.mutation<ApproveRejectInvestmentResponse, ApproveRejectInvestmentRequest>({
            query: (body) => ({
                url: '/admin/approve-reject-investment',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['AdminInvestments'],
        }),

        // Project Management
        getAllProjects: builder.query<ProjectListResponse, { page?: number; limit?: number; search?: string }>({
            query: (params) => ({
                url: '/admin/get-all-project',
                params: {
                    page: params.page || 1,
                    limit: params.limit || 10,
                    ...(params.search && { search: params.search }),
                },
            }),
            providesTags: ['AdminProjects'],
        }),

        createProjectStep1: builder.mutation<CreateProjectResponse, CreateProjectRequest>({
            query: (projectData) => ({
                url: '/admin/create-project',
                method: 'POST',
                body: projectData,
            }),
            invalidatesTags: ['AdminProjects'],
        }),

        updateProject: builder.mutation<CreateProjectResponse, CreateProjectRequest>({
            query: (projectData) => ({
                url: '/admin/update-project',
                method: 'POST',
                body: projectData,
            }),
            invalidatesTags: ['AdminProjects'],
        }),

        addProjectImages: builder.mutation<AddProjectImagesResponse, AddProjectImagesRequest>({
            query: ({ project_id, images }) => {
                // Validate project_id
                if (!project_id || project_id <= 0) {
                    throw new Error('Invalid project ID. Project ID must be a positive number.');
                }

                // Ensure images is an array
                let imagesArray: File[] = [];
                if (Array.isArray(images)) {
                    imagesArray = images;
                } else if (images) {
                    // Convert FileList or single File to array
                    imagesArray = Array.from(images as any);
                }

                // Validate images array
                if (!imagesArray || imagesArray.length === 0) {
                    throw new Error('No images provided. Please select at least one image.');
                }

                // Check authentication token
                const token = localStorage.getItem('diasporawise_admin_token');
                if (!token) {
                    throw new Error('No authentication token found. Please login first.');
                }

                const formData = new FormData();
                formData.append('project_id', project_id.toString());

                // Add images with proper validation
                imagesArray.forEach((image) => {
                    // Validate file type
                    if (!image.type.startsWith('image/')) {
                        throw new Error(`File ${image.name} is not a valid image file. Only image files are allowed.`);
                    }

                    // Validate file size (max 10MB)
                    if (image.size > 10 * 1024 * 1024) {
                        throw new Error(`File ${image.name} is too large. Maximum size is 10MB.`);
                    }

                    // Validate file size (min 1KB)
                    if (image.size < 1024) {
                        throw new Error(`File ${image.name} is too small. Minimum size is 1KB.`);
                    }

                    formData.append('images', image);
                });

                console.log('FormData contents:');
                for (let [key, value] of formData.entries()) {
                    console.log(key, value);
                }

                console.log('Request details:', {
                    url: '/admin/add-project-images',
                    method: 'POST',
                    project_id: project_id,
                    imageCount: imagesArray.length,
                    imageTypes: imagesArray.map(img => img.type),
                    imageSizes: imagesArray.map(img => img.size),
                    token: token ? `${token.substring(0, 20)}...` : 'No token'
                });

                return {
                    url: '/admin/add-project-images',
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: ['AdminProjects'],
        }),

        addProjectDocuments: builder.mutation<AddProjectDocumentsResponse, AddProjectDocumentsRequest>({
            query: ({ project_id, document_name, document }) => {
                const formData = new FormData();
                formData.append('project_id', project_id);
                formData.append('document_name', document_name);
                formData.append('document', document);

                return {
                    url: '/admin/add-project-documents',
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: ['AdminProjects'],
        }),

        deleteProject: builder.mutation<DeleteProjectResponse, DeleteProjectRequest>({
            query: (body) => ({
                url: '/admin/delete-project',
                method: 'DELETE',
                body,
            }),
            invalidatesTags: ['AdminProjects'],
        }),

        bulkDeleteProjects: builder.mutation<{ success: boolean; message: string }, { project_ids: number[]; delete_type: 'soft' | 'hard' }>({
            query: (body) => ({
                url: '/admin/bulk-delete-projects',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['AdminProjects'],
        }),

        deleteProjectDocument: builder.mutation<{ success: boolean; message: string }, { document_id: number; project_id: number }>({
            query: ({ document_id, project_id }) => ({
                url: '/admin/delete-project-document',
                method: 'POST',
                body: { document_id, project_id },
            }),
            invalidatesTags: ['AdminProjects'],
        }),

        deleteProjectImage: builder.mutation<{ success: boolean; message: string }, { image_id: number; project_id: number }>({
            query: ({ image_id, project_id }) => ({
                url: '/admin/delete-project-image',
                method: 'POST',
                body: { image_id, project_id },
            }),
            invalidatesTags: ['AdminProjects'],
        }),

        // Super Admin Dashboard Details
        getSuperAdminDashboardDetails: builder.query<DashboardDetails, void>({
            query: () => '/super-admin/dashboard-details',
            providesTags: ['AdminDashboard'],
        }),

        // Location search endpoint
        searchLocation: builder.query<LocationSearchResponse, { input: string }>({
            query: ({ input }) => `/admin/search-location?input=${encodeURIComponent(input)}`,
            providesTags: ['LocationSearch'],
        }),

        // Get latitude and longitude endpoint
        getLatLong: builder.query<LatLongResponse, { address: string }>({
            query: ({ address }) => `/admin/get-lat-long?address=${encodeURIComponent(address)}`,
            providesTags: ['LatLong'],
        }),

        // Get project details endpoint
        getProjectDetails: builder.query<ProjectDetailsResponse, { project_id: number }>({
            query: ({ project_id }) => ({
                url: '/api/get-project-details',
                method: 'POST',
                body: { project_id },
            }),
            providesTags: ['ProjectDetails'],
        }),

        // Blogs management endpoints
        getBlogs: builder.query<BlogsResponse, BlogsParams>({
            query: (params = {}) => {
                const searchParams = new URLSearchParams();
                if (params.page) searchParams.append('page', params.page.toString());
                if (params.limit) searchParams.append('limit', params.limit.toString());
                if (params.status && params.status !== 'all') searchParams.append('status', params.status);
                if (params.category && params.category !== 'all') searchParams.append('category', params.category);
                if (params.search) searchParams.append('search', params.search);

                const queryString = searchParams.toString();
                return `/admin/blogs${queryString ? `?${queryString}` : ''}`;
            },
            providesTags: ['Blogs'],
        }),
        createBlog: builder.mutation<BlogCreateResponse, FormData>({
            query: (formData) => ({
                url: '/admin/blogs/create',
                method: 'POST',
                body: formData,
            }),
            invalidatesTags: ['Blogs'],
        }),
        updateBlog: builder.mutation<BlogCreateResponse, FormData>({
            query: (formData) => ({
                url: '/admin/blogs/update',
                method: 'POST',
                body: formData,
            }),
            invalidatesTags: ['Blogs'],
        }),
        deleteBlog: builder.mutation<{ success: boolean; message: string }, number>({
            query: (blogId) => ({
                url: `/admin/blogs/${blogId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Blogs'],
        }),

        // Public blogs endpoint
        getPublicBlogs: builder.query<BlogsResponse, void>({
            query: () => '/api/blogs',
            providesTags: ['PublicBlogs'],
        }),

        // Featured blogs endpoint
        getFeaturedBlogs: builder.query<{ success: boolean; status: number; message: string; data: any[] }, void>({
            query: () => '/api/blogs',
            providesTags: ['FeaturedBlogs'],
        }),

        // Blog detail by slug endpoint
        getBlogBySlug: builder.query<{ success: boolean; status: number; message: string; data: any }, string>({
            query: (slug) => `/api/blogs/slug/${slug}`,
            providesTags: (_result, _error, slug) => [{ type: 'BlogDetail', id: slug }],
        }),

        // Public projects endpoint
        getPublicProjects: builder.query<{ success: boolean; status: number; message: string; data: any[] }, void>({
            query: () => '/api/get-all-project-for-web',
            providesTags: ['PublicProjects'],
        }),

        // Public statistics endpoint
        getPublicStatistics: builder.query<DashboardDetails, void>({
            query: () => '/api/public-statistics',
            providesTags: ['PublicProjects'],
        }),

        // User-facing Investment endpoints
        getMyInvestments: builder.query<UserInvestmentsResponse, void>({
            query: () => ({
                url: '/api/my-investments',
                method: 'GET',
            }),
            providesTags: ['Investment'],
        }),

        getMyInvestmentStats: builder.query<UserInvestmentStatsResponse, void>({
            query: () => ({
                url: '/api/my-investment-stats',
                method: 'GET',
            }),
            providesTags: ['Investment'],
        }),

        createInvestment: builder.mutation<CreateInvestmentResponse, CreateInvestmentRequest>({
            query: ({ project_id, amount_invested, file }) => {
                const formData = new FormData();
                formData.append('project_id', project_id.toString());
                formData.append('amount_invested', amount_invested.toString());
                formData.append('file', file);

                return {
                    url: '/api/create-investment',
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: ['Investment', 'Project'],
        }),

        createWalletInvestment: builder.mutation<CreateWalletInvestmentResponse, CreateWalletInvestmentRequest>({
            query: (body) => ({
                url: '/api/create-wallet-investment',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Investment', 'Project', 'WalletTransactions', 'WalletStats'],
        }),

        getUserProjects: builder.query<UserProjectsResponse, UserProjectsParams>({
            query: (params = {}) => {
                const searchParams = new URLSearchParams();
                if (params.page) searchParams.append('page', params.page.toString());
                if (params.limit) searchParams.append('limit', params.limit.toString());
                if (params.category_id) searchParams.append('category_id', params.category_id.toString());
                if (params.search) searchParams.append('search', params.search);

                const queryString = searchParams.toString();
                return {
                    url: `/api/get-all-projects${queryString ? `?${queryString}` : ''}`,
                    method: 'GET',
                };
            },
            providesTags: ['Project'],
        }),

        getProjectStatus: builder.query<ProjectStatusResponse, number>({
            query: (project_id) => ({
                url: `/api/project-status/${project_id}`,
                method: 'GET',
            }),
            providesTags: ['Project'],
        }),

        // Legacy endpoints (keeping for backward compatibility)
        getInvestments: builder.query<UserInvestmentsResponse, { userId?: string }>({
            query: () => ({
                url: '/api/my-investments',
                method: 'GET',
            }),
            providesTags: ['Investment'],
        }),

        // Profile endpoints
        getProfile: builder.query<any, { role: string }>({
            query: ({ role }) => {
                if (role === 'USER' || role === 'DEALER') {
                    return '/api/profile';
                } else {
                    return '/admin/profile';
                }
            },
            providesTags: ['Profile'],
        }),
        updateProfile: builder.mutation<any, { formData: FormData; role?: string }>({
            query: ({ formData, role }) => {
                if (role === 'USER' || role === 'DEALER') {
                    return {
                        url: '/api/profile/update',
                        method: 'POST',
                        body: formData,
                    };
                } else {
                    return {
                        url: '/admin/profile/update',
                        method: 'POST',
                        body: formData,
                    };
                }
            },
            invalidatesTags: ['Profile'],
        }),
        updateSuperAdminProfile: builder.mutation<any, { formData: FormData }>({
            query: ({ formData }) => ({
                url: '/admin/profile/update',
                method: 'POST',
                body: formData,
            }),
            invalidatesTags: ['Profile'],
        }),
        // User account details endpoints
        updateUserAccountDetails: builder.mutation<any, { 
            account_holder_name: string;
            bank_name: string;
            account_number: string;
        }>({
            query: (body) => ({
                url: '/api/account-details-update',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Profile'],
        }),

        // Notification Management
        getNotifications: builder.query<NotificationListResponse, void>({
            query: () => '/admin/get-notifications',
            providesTags: ['Notifications'],
        }),
        scheduleNotification: builder.mutation<NotificationResponse, ScheduleNotificationRequest>({
            query: (body) => ({
                url: '/admin/schedule-notification',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Notifications'],
        }),
        updateNotification: builder.mutation<NotificationResponse, UpdateNotificationRequest>({
            query: (body) => ({
                url: '/admin/update-notification',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Notifications'],
        }),
        deleteNotification: builder.mutation<NotificationResponse, number>({
            query: (notification_id) => ({
                url: `/admin/delete-notification/${notification_id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Notifications'],
        }),

        // User Details
        getUserDetails: builder.query<any, { user_id: number }>({
            query: (body) => ({
                url: '/admin/user-details',
                method: 'POST',
                body,
            }),
            providesTags: ['UserDetails'],
        }),

        // CTA Management
        getCTAPopups: builder.query<CTAListResponse, void>({
            query: () => '/admin/get-all-cta-popup',
            providesTags: ['CTAPopups'],
        }),
        createCTAPopup: builder.mutation<CTAResponse, FormData>({
            query: (body) => ({
                url: '/admin/create-cta-popup',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['CTAPopups'],
        }),
        updateCTAPopup: builder.mutation<CTAResponse, FormData>({
            query: (body) => ({
                url: '/admin/update-cta-popup',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['CTAPopups'],
        }),
        deleteCTAPopup: builder.mutation<CTAResponse, number>({
            query: (id) => ({
                url: `/admin/delete-cta-popup/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['CTAPopups'],
        }),

        // Contact Management
        getContactSubmissions: builder.query<ContactSubmissionsResponse, ContactSubmissionsParams>({
            query: (params = {}) => {
                const searchParams = new URLSearchParams();
                if (params.page) searchParams.append('page', params.page.toString());
                if (params.limit) searchParams.append('limit', params.limit.toString());
                if (params.status && params.status !== 'all') searchParams.append('status', params.status);
                if (params.is_read !== undefined) searchParams.append('is_read', params.is_read.toString());

                const queryString = searchParams.toString();
                return `/admin/contact-submissions${queryString ? `?${queryString}` : ''}`;
            },
            providesTags: ['ContactSubmissions'],
        }),
        getContactSubmission: builder.query<ContactSubmissionResponse, number>({
            query: (id) => `/admin/contact-submissions/${id}`,
            providesTags: ['ContactSubmissions'],
        }),
        updateContactSubmission: builder.mutation<UpdateContactSubmissionResponse, UpdateContactSubmissionRequest>({
            query: (body) => ({
                url: '/admin/contact-submissions/update-status',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['ContactSubmissions'],
        }),
        deleteContactSubmission: builder.mutation<DeleteContactSubmissionResponse, number>({
            query: (id) => ({
                url: `/admin/contact-submissions/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['ContactSubmissions'],
        }),
        createContactSubmission: builder.mutation<ContactResponse, CreateContactRequest>({
            query: (body) => ({
                url: '/api/contact/create-submission',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['ContactSubmissions'],
        }),
        markContactAsRead: builder.mutation<ContactResponse, number>({
            query: (id) => ({
                url: '/api/contact/mark-as-read',
                method: 'POST',
                body: { id },
            }),
            invalidatesTags: ['ContactSubmissions'],
        }),
        replyToContact: builder.mutation<ContactResponse, { id: number; reply: string }>({
            query: (body) => ({
                url: '/api/contact/reply-to-submission',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['ContactSubmissions'],
        }),
        getContactStatistics: builder.query<ContactStatisticsResponse, void>({
            query: () => '/api/contact/statistics',
            providesTags: ['ContactStatistics'],
        }),

        // Solar Lead Management
        createSolarLead: builder.mutation<SolarLeadResponse, CreateSolarLeadRequest>({
            query: (body) => ({
                url: '/api/solar/signup',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['SolarLeads'],
        }),
        getSolarLeads: builder.query<SolarLeadsResponse, {
            page?: number;
            limit?: number;
            country?: string;
            state?: string;
            lga?: string;
            status?: string;
            search?: string;
        }>({
            query: (params = {}) => {
                const searchParams = new URLSearchParams();
                if (params.page) searchParams.append('page', params.page.toString());
                if (params.limit) searchParams.append('limit', params.limit.toString());
                if (params.country) searchParams.append('country', params.country);
                if (params.state) searchParams.append('state', params.state);
                if (params.lga) searchParams.append('local_government_area', params.lga);
                if (params.status) searchParams.append('status', params.status);
                if (params.search) searchParams.append('search', params.search);
                const queryString = searchParams.toString();
                return `/admin/solar/leads${queryString ? `?${queryString}` : ''}`;
            },
            providesTags: ['SolarLeads'],
        }),
        getSolarLead: builder.query<SolarLeadResponse, number>({
            query: (id) => `/admin/solar/leads/${id}`,
            providesTags: ['SolarLeads'],
        }),
        updateSolarLeadStatus: builder.mutation<SolarLeadResponse, UpdateSolarLeadStatusRequest>({
            query: (body) => ({
                url: '/admin/solar/leads/update-status',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['SolarLeads'],
        }),
        scheduleSolarMeeting: builder.mutation<SolarLeadResponse, ScheduleSolarMeetingRequest>({
            query: (body) => ({
                url: '/admin/solar/leads/schedule-meeting',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['SolarLeads'],
        }),
        updateSolarMeeting: builder.mutation<SolarLeadResponse, UpdateSolarMeetingRequest>({
            query: (body) => ({
                url: '/admin/solar/leads/update-meeting',
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['SolarLeads'],
        }),
        updateSolarLeadNotes: builder.mutation<SolarLeadResponse, UpdateSolarLeadNotesRequest>({
            query: (body) => ({
                url: '/admin/solar/leads/update-notes',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['SolarLeads'],
        }),
        deleteSolarLead: builder.mutation<SolarLeadResponse, number>({
            query: (id) => ({
                url: `/admin/solar/leads/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['SolarLeads'],
        }),

        // Testimonials Management
        getTestimonials: builder.query<TestimonialsListResponse, void>({
            query: () => '/admin/testimonials',
            providesTags: ['Testimonials'],
        }),
        createTestimonial: builder.mutation<TestimonialResponse, CreateTestimonialRequest>({
            query: (body) => ({
                url: '/admin/testimonials/create',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Testimonials'],
        }),
        updateTestimonial: builder.mutation<TestimonialResponse, UpdateTestimonialRequest>({
            query: (body) => ({
                url: '/admin/testimonials/update',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Testimonials'],
        }),
        updateTestimonialStatus: builder.mutation<TestimonialResponse, { id: number; status: 'pending' | 'approved' | 'rejected' }>({
            query: (body) => ({
                url: '/admin/testimonials/update-status',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Testimonials'],
        }),
        toggleTestimonialFeatured: builder.mutation<TestimonialResponse, number>({
            query: (id) => ({
                url: `/admin/testimonials/toggle-featured/${id}`,
                method: 'POST',
            }),
            invalidatesTags: ['Testimonials'],
        }),
        deleteTestimonial: builder.mutation<TestimonialResponse, number>({
            query: (id) => ({
                url: `/admin/testimonials/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Testimonials'],
        }),

        // Public Testimonials
        getPublicTestimonials: builder.query<PublicTestimonialsResponse, void>({
            query: () => '/api/testimonials',
            providesTags: ['PublicTestimonials'],
        }),

        // Wallet Transactions
        getWalletTransactions: builder.query<WalletTransactionsResponse, WalletTransactionsParams>({
            query: (params) => ({
                url: '/admin/wallet-transactions',
                params: {
                    page: params.page || 1,
                    limit: params.limit || 10,
                    ...(params.status && params.status !== 'all' && { status: params.status }),
                    ...(params.transaction_type && params.transaction_type !== 'all' && { transaction_type: params.transaction_type }),
                },
            }),
            providesTags: ['WalletTransactions'],
        }),

        approveRejectWalletTransaction: builder.mutation<ApproveRejectWalletTransactionResponse, ApproveRejectWalletTransactionRequest>({
            query: (body) => ({
                url: '/admin/wallet-transactions/approve-reject',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['WalletTransactions', 'WalletStats'],
        }),

        addFunds: builder.mutation<AddFundsResponse, AddFundsRequest>({
            query: (body) => ({
                url: '/admin/wallet-transactions/add-funds',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['WalletTransactions', 'WalletStats'],
        }),

        getWalletStats: builder.query<WalletStatsResponse, void>({
            query: () => '/admin/wallet-transactions/stats',
            providesTags: ['WalletStats'],
        }),

        // Project Documents
        addProjectDocument: builder.mutation<ProjectDocumentResponse, AddProjectDocumentRequest>({
            query: (body) => {
                const formData = new FormData();
                formData.append('project_id', body.project_id);
                formData.append('document_name', body.document_name);
                formData.append('document', body.document);

                return {
                    url: '/admin/add-project-documents',
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: ['ProjectDetails'],
        }),

        // Child Sponsorship Management
        getAllChildren: builder.query<ChildListResponse, { page?: number; limit?: number; search?: string; status?: string }>({
            query: (params) => ({
                url: '/admin/children',
                params: {
                    page: params.page || 1,
                    limit: params.limit || 10,
                    ...(params.search && { search: params.search }),
                    ...(params.status && params.status !== 'all' && { status: params.status }),
                },
            }),
            providesTags: ['ChildSponsorship'],
        }),

        getChildDetails: builder.query<ChildResponse, number>({
            query: (child_id) => {
                console.log('API Slice - Fetching child details for ID:', child_id);
                return `/admin/children/${child_id}`;
            },
            providesTags: ['ChildSponsorship'],
        }),

        createChild: builder.mutation<ChildResponse, CreateChildRequest>({
            query: (body) => {
                const formData = new FormData();
                formData.append('name', body.name);
                formData.append('age', body.age.toString());
                formData.append('location', body.location);
                formData.append('education_stage', body.education_stage);
                formData.append('bio', body.bio);
                if (body.currency_code) {
                    formData.append('currency_code', body.currency_code);
                }
                formData.append('weekly_funding_required', body.weekly_funding_required.toString());
                formData.append('monthly_funding_required', body.monthly_funding_required.toString());
                formData.append('yearly_funding_required', body.yearly_funding_required.toString());
                formData.append('verification_notes', body.verification_notes || '');
                if (body.photo) {
                    formData.append('profile_image', body.photo);
                }

                return {
                    url: '/admin/children/create',
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: ['ChildSponsorship', 'ChildSponsorshipStats'],
        }),

        updateChild: builder.mutation<ChildResponse, UpdateChildRequest>({
            query: (body) => {
                console.log('updateChild API call with body:', body);

                const formData = new FormData();
                // Send child_id in FormData as required by the API
                formData.append('child_id', body.child_id.toString());
                formData.append('name', body.name);
                formData.append('age', body.age.toString());
                formData.append('location', body.location);
                formData.append('education_stage', body.education_stage);
                formData.append('bio', body.bio);
                if (body.currency_code) {
                    formData.append('currency_code', body.currency_code);
                }
                formData.append('weekly_funding_required', body.weekly_funding_required.toString());
                formData.append('monthly_funding_required', body.monthly_funding_required.toString());
                formData.append('yearly_funding_required', body.yearly_funding_required.toString());
                formData.append('verification_notes', body.verification_notes || '');
                formData.append('status', body.status || 'available');
                if (body.photo) {
                    formData.append('profile_image', body.photo);
                }

                const requestConfig = {
                    url: `/admin/children/update`,
                    method: 'PUT', // Use PUT method as shown in the working curl example
                    body: formData,
                };

                // Debug: Log FormData contents
                console.log('FormData contents:');
                for (let [key, value] of formData.entries()) {
                    console.log(`${key}:`, value);
                }

                console.log('updateChild request config:', requestConfig);
                console.log('updateChild API endpoint:', `/admin/children/${body.child_id}`);
                return requestConfig;
            },
            invalidatesTags: ['ChildSponsorship', 'ChildSponsorshipStats'],
        }),

        verifyChild: builder.mutation<ChildResponse, VerifyChildRequest>({
            query: (body) => {
                const requestBody = {
                    is_verified: body.verified,
                    verification_notes: body.verification_notes || "Child verification updated by admin"
                };
                console.log('verifyChild API call:', {
                    url: `/admin/children/${body.child_id}/verify`,
                    method: 'POST',
                    body: requestBody
                });
                return {
                    url: `/admin/children/${body.child_id}/verify`,
                    method: 'POST',
                    body: requestBody,
                };
            },
            invalidatesTags: ['ChildSponsorship', 'ChildSponsorshipStats'],
        }),

        deleteChild: builder.mutation<ChildResponse, DeleteChildRequest>({
            query: (body) => ({
                url: `/admin/children/${body.child_id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['ChildSponsorship', 'ChildSponsorshipStats'],
        }),

        // Sponsorship Updates Management
        getSponsorshipUpdates: builder.query<SponsorshipUpdatesListResponse, { page?: number; limit?: number; search?: string; type?: string; status?: string }>({
            query: (params) => ({
                url: '/admin/sponsorship-updates',
                params: {
                    page: params.page || 1,
                    limit: params.limit || 10,
                    ...(params.search && { search: params.search }),
                    ...(params.type && params.type !== 'all' && { type: params.type }),
                    ...(params.status && params.status !== 'all' && { status: params.status }),
                },
            }),
            providesTags: ['ChildSponsorshipUpdates'],
        }),

        createSponsorshipUpdate: builder.mutation<SponsorshipUpdateResponse, CreateSponsorshipUpdateRequest>({
            query: (body) => {
                const formData = new FormData();
                formData.append('child_id', body.child_id.toString());
                formData.append('title', body.title);
                formData.append('description', body.description);
                formData.append('update_type', body.update_type);
                formData.append('update_date', body.update_date);

                // Add content if provided
                if (body.content) {
                    formData.append('content', body.content);
                }

                if (body.images) {
                    body.images.forEach((image) => {
                        formData.append('images', image);
                    });
                }
                if (body.videos) {
                    body.videos.forEach((video) => {
                        formData.append('videos', video);
                    });
                }

                // Debug: Log FormData contents
                console.log('FormData contents:');
                for (let [key, value] of formData.entries()) {
                    if (value instanceof File) {
                        console.log(`${key}: File(${value.name}, ${value.size} bytes, ${value.type})`);
                    } else {
                        console.log(`${key}: ${value}`);
                    }
                }

                return {
                    url: '/admin/sponsorship-updates/create',
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: ['ChildSponsorshipUpdates', 'ChildSponsorshipStats'],
        }),

        updateSponsorshipUpdate: builder.mutation<SponsorshipUpdateResponse, UpdateSponsorshipUpdateRequest>({
            query: (body) => {
                const formData = new FormData();
                formData.append('update_id', (body.update_id || '').toString());
                // Remove child_id as it's not allowed in update endpoint
                // formData.append('child_id', (body.child_id || '').toString());
                formData.append('title', body.title || '');
                formData.append('description', body.description || '');
                // Remove update_type as it's not allowed in update endpoint
                // formData.append('update_type', body.update_type || 'progress_report');
                formData.append('update_date', body.update_date || new Date().toISOString().split('T')[0]);

                // Add content if provided
                if (body.content) {
                    formData.append('content', body.content);
                }

                if (body.images) {
                    body.images.forEach((image) => {
                        formData.append('images', image);
                    });
                }
                if (body.videos) {
                    body.videos.forEach((video) => {
                        formData.append('videos', video);
                    });
                }

                console.log('Attempting to update sponsorship update with ID:', body.update_id);
                console.log('FormData being sent:', {
                    update_id: body.update_id,
                    title: body.title,
                    description: body.description,
                    update_date: body.update_date,
                    content: body.content,
                    images_count: body.images?.length || 0,
                    videos_count: body.videos?.length || 0
                });

                return {
                    url: '/admin/sponsorship-updates/update',
                    method: 'PUT',
                    body: formData,
                };
            },
            invalidatesTags: ['ChildSponsorshipUpdates', 'ChildSponsorshipStats'],
        }),

        deleteSponsorshipUpdate: builder.mutation<SponsorshipUpdateResponse, DeleteSponsorshipUpdateRequest>({
            query: (body) => ({
                url: `/admin/sponsorship-updates/${body.update_id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['ChildSponsorshipUpdates'],
        }),


        // Membership Plans Management
        getAllMembershipPlans: builder.query<MembershipPlanListResponse, { is_active?: boolean }>({
            query: (params) => ({
                url: '/admin/membership-plans',
                params: params.is_active !== undefined ? { is_active: params.is_active.toString() } : {},
            }),
            providesTags: ['MembershipPlans'],
        }),

        getMembershipPlan: builder.query<MembershipPlanResponse, number>({
            query: (plan_id) => `/admin/membership-plans/${plan_id}`,
            providesTags: ['MembershipPlans'],
        }),

        createMembershipPlan: builder.mutation<MembershipPlanResponse, CreateMembershipPlanRequest>({
            query: (body) => ({
                url: '/admin/membership-plans/create',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['MembershipPlans'],
        }),

        updateMembershipPlan: builder.mutation<MembershipPlanResponse, UpdateMembershipPlanRequest>({
            query: (body) => {
                const { plan_id, ...updateData } = body;
                return {
                    url: `/admin/membership-plans/${plan_id}`,
                    method: 'PUT',
                    body: updateData,
                };
            },
            invalidatesTags: ['MembershipPlans'],
        }),

        deleteMembershipPlan: builder.mutation<{ success: boolean; status: number; message: string }, number>({
            query: (plan_id) => ({
                url: `/admin/membership-plans/${plan_id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, arg) => {
                // Only invalidate if deletion was actually successful
                if (!error && result?.success !== false) {
                    return ['MembershipPlans'];
                }
                return [];
            },
        }),

        // Apartment Management
        getAllApartments: builder.query<ApartmentListResponse, { is_active?: boolean; location?: string }>({
            query: (params) => ({
                url: '/admin/apartments',
                params: {
                    ...(params.is_active !== undefined && { is_active: params.is_active.toString() }),
                    ...(params.location && { location: params.location }),
                },
            }),
            providesTags: ['Apartments'],
        }),

        getApartment: builder.query<ApartmentResponse, number>({
            query: (apartment_id) => `/admin/apartments/${apartment_id}`,
            providesTags: ['Apartments'],
        }),

        createApartment: builder.mutation<ApartmentResponse, CreateApartmentRequest>({
            query: (body) => {
                const formData = new FormData();
                formData.append('name', body.name);
                if (body.description) formData.append('description', body.description);
                formData.append('location', body.location);
                if (body.address) formData.append('address', body.address);
                formData.append('capacity', body.capacity.toString());
                if (body.amenities) formData.append('amenities', body.amenities);

                if (body.images) {
                    const imageFiles: File[] = [];
                    const imageUrls: string[] = [];
                    
                    body.images.forEach((image) => {
                        if (image instanceof File) {
                            imageFiles.push(image);
                        } else if (typeof image === 'string') {
                            imageUrls.push(image);
                        }
                    });
                    
                    // Append files
                    imageFiles.forEach((file) => {
                        formData.append('images', file);
                    });
                    
                    // If we have URL strings, append them as JSON array
                    if (imageUrls.length > 0) {
                        formData.append('images', JSON.stringify(imageUrls));
                    }
                }

                return {
                    url: '/admin/apartments/create',
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: ['Apartments'],
        }),

        updateApartment: builder.mutation<ApartmentResponse, UpdateApartmentRequest>({
            query: (body) => {
                const formData = new FormData();
                const { apartment_id, ...updateData } = body;

                if (updateData.name !== undefined) formData.append('name', updateData.name);
                if (updateData.description !== undefined) formData.append('description', updateData.description || '');
                if (updateData.location !== undefined) formData.append('location', updateData.location);
                if (updateData.address !== undefined) formData.append('address', updateData.address || '');
                if (updateData.capacity !== undefined) formData.append('capacity', updateData.capacity.toString());
                if (updateData.amenities !== undefined) formData.append('amenities', updateData.amenities || '');
                if (updateData.is_active !== undefined) formData.append('is_active', updateData.is_active.toString());
                if (updateData.is_available !== undefined) formData.append('is_available', updateData.is_available.toString());

                if (updateData.images) {
                    const imageFiles: File[] = [];
                    const imageUrls: string[] = [];
                    
                    updateData.images.forEach((image) => {
                        if (image instanceof File) {
                            imageFiles.push(image);
                        } else if (typeof image === 'string') {
                            imageUrls.push(image);
                        }
                    });
                    
                    if (imageFiles.length > 0) {
                        imageFiles.forEach((file) => {
                            formData.append('images', file);
                        });
                    }
                    
                    if (imageUrls.length > 0) {
                        // Send existing URLs as JSON string in the body
                        formData.append('images', JSON.stringify(imageUrls));
                    }
                }

                return {
                    url: `/admin/apartments/${apartment_id}`,
                    method: 'PUT',
                    body: formData,
                };
            },
            invalidatesTags: ['Apartments'],
        }),

        deleteApartment: builder.mutation<{ success: boolean; status: number; message: string }, number>({
            query: (apartment_id) => ({
                url: `/admin/apartments/${apartment_id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Apartments'],
        }),

        deleteApartmentImage: builder.mutation<{
            success: boolean;
            status: number;
            message: string;
            data: {
                apartment_id: number;
                deleted_image: string;
                remaining_images: string[];
                total_images: number;
            };
        }, { apartment_id: number; image_filename: string }>({
            query: ({ apartment_id, image_filename }) => ({
                url: `/admin/apartments/${apartment_id}/images`,
                method: 'DELETE',
                body: { image_filename },
            }),
            invalidatesTags: ['Apartments'],
        }),

        manageApartmentAvailability: builder.mutation<ManageApartmentAvailabilityResponse, ManageApartmentAvailabilityRequest>({
            query: (body) => ({
                url: `/admin/apartments/${body.apartment_id}/availability`,
                method: 'POST',
                body: {
                    dates: body.dates,
                    is_available: body.is_available,
                    reason: body.reason || null,
                },
            }),
            invalidatesTags: ['Apartments'],
        }),

        // Booking Management
        getAllBookings: builder.query<BookingListResponse, BookingListParams>({
            query: (params) => ({
                url: '/admin/bookings',
                params: {
                    page: params.page || 1,
                    limit: params.limit || 20,
                    ...(params.status && params.status !== 'all' && { status: params.status }),
                    ...(params.user_id && { user_id: params.user_id }),
                    ...(params.start_date && { start_date: params.start_date }),
                    ...(params.end_date && { end_date: params.end_date }),
                },
            }),
            providesTags: ['Bookings'],
        }),

        getBookingDetails: builder.query<BookingResponse, number>({
            query: (booking_id) => `/admin/bookings/${booking_id}`,
            providesTags: ['Bookings'],
        }),

        updateBookingStatus: builder.mutation<BookingResponse, UpdateBookingStatusRequest>({
            query: (body) => {
                const { booking_id, ...updateData } = body;
                return {
                    url: `/admin/bookings/${booking_id}/status`,
                    method: 'PUT',
                    body: updateData,
                };
            },
            invalidatesTags: ['Bookings'],
        }),

        processRefund: builder.mutation<ProcessRefundResponse, ProcessRefundRequest>({
            query: (body) => {
                const { booking_id, ...refundData } = body;
                return {
                    url: `/admin/bookings/${booking_id}/refund`,
                    method: 'POST',
                    body: refundData,
                };
            },
            invalidatesTags: ['Bookings', 'WalletTransactions', 'WalletStats'],
        }),

        // Membership Management (User Memberships)
        getAllMemberships: builder.query<MembershipListResponse, MembershipListParams>({
            query: (params) => ({
                url: '/admin/memberships',
                params: {
                    page: params.page || 1,
                    limit: params.limit || 20,
                    ...(params.status && params.status !== 'all' && { status: params.status }),
                    ...(params.user_id && { user_id: params.user_id }),
                },
            }),
            providesTags: ['Memberships'],
        }),

        updateMembershipStatus: builder.mutation<MembershipResponse, UpdateMembershipStatusRequest>({
            query: (body) => {
                const { membership_id, ...updateData } = body;
                return {
                    url: `/admin/memberships/${membership_id}/status`,
                    method: 'PUT',
                    body: updateData,
                };
            },
            invalidatesTags: ['Memberships'],
        }),

        // Marketplace Listings
        getMarketplaceListings: builder.query<MarketplaceListingsResponse, MarketplaceListingParams>({
            query: (params) => ({
                url: '/admin/marketplace/listings',
                params: {
                    page: params.page || 1,
                    limit: params.limit || 10,
                    ...(params.status && params.status !== 'all' && { status: params.status }),
                    ...(params.listing_type && params.listing_type !== 'all' && { listing_type: params.listing_type }),
                    ...(params.search && { search: params.search }),
                },
            }),
            providesTags: ['MarketplaceListings'],
        }),

        getMarketplaceListing: builder.query<MarketplaceListingResponse, number>({
            query: (listing_id) => `/admin/marketplace/listings/${listing_id}`,
            providesTags: ['MarketplaceListings'],
        }),

        getSoldListings: builder.query<MarketplaceListingsResponse, SoldListingsParams>({
            query: (params) => ({
                url: '/admin/marketplace/listings/sold',
                params: {
                    page: params.page || 1,
                    limit: params.limit || 10,
                    ...(params.listing_type && params.listing_type !== 'all' && { listing_type: params.listing_type }),
                    ...(params.listing_source && params.listing_source !== 'all' && { listing_source: params.listing_source }),
                },
            }),
            providesTags: ['MarketplaceListings'],
        }),

        getPendingListings: builder.query<MarketplaceListingsResponse, PendingListingsParams>({
            query: (params) => ({
                url: '/admin/marketplace/listings/pending',
                params: {
                    page: params.page || 1,
                    limit: params.limit || 10,
                    ...(params.listing_type && params.listing_type !== 'all' && { listing_type: params.listing_type }),
                },
            }),
            providesTags: ['MarketplaceListings'],
        }),

        approveListing: builder.mutation<{ success: boolean; status: number; message: string; data: { listing_id: number; approval_status: string; status: string } }, ApproveListingRequest>({
            query: (body) => ({
                url: '/admin/marketplace/listings/approve',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['MarketplaceListings'],
        }),

        rejectListing: builder.mutation<{ success: boolean; status: number; message: string; data: { listing_id: number; approval_status: string; rejection_reason: string } }, RejectListingRequest>({
            query: (body) => ({
                url: '/admin/marketplace/listings/reject',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['MarketplaceListings'],
        }),

        createMarketplaceListing: builder.mutation<MarketplaceListingResponse, CreateMarketplaceListingRequest>({
            query: (body) => {
                const formData = new FormData();
                Object.keys(body).forEach(key => {
                    if (key === 'images' && body.images) {
                        body.images.forEach((file: File) => {
                            formData.append('images', file);
                        });
                    } else if (key === 'tags' && body.tags) {
                        body.tags.forEach((tag: string) => {
                            formData.append('tags[]', tag);
                        });
                    } else if (key === 'car_details' && body.car_details) {
                        // Always send car_details, even if empty object
                        const carDetails = body.car_details;
                        if (Object.keys(carDetails).length > 0) {
                            Object.keys(carDetails).forEach(carKey => {
                                formData.append(`car_details[${carKey}]`, carDetails[carKey]);
                            });
                        } else {
                            // Send empty object as JSON string to ensure backend receives an object
                            formData.append('car_details', JSON.stringify({}));
                        }
                    } else if (key === 'house_details' && body.house_details) {
                        // Always send house_details, even if empty object
                        const houseDetails = body.house_details;
                        if (Object.keys(houseDetails).length > 0) {
                            Object.keys(houseDetails).forEach(houseKey => {
                                formData.append(`house_details[${houseKey}]`, houseDetails[houseKey]);
                            });
                        } else {
                            // Send empty object as JSON string to ensure backend receives an object
                            formData.append('house_details', JSON.stringify({}));
                        }
                    } else if (body[key as keyof CreateMarketplaceListingRequest] !== undefined) {
                        formData.append(key, String(body[key as keyof CreateMarketplaceListingRequest]));
                    }
                });
                return {
                    url: '/admin/marketplace/listings',
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: ['MarketplaceListings'],
        }),

        updateMarketplaceListing: builder.mutation<MarketplaceListingResponse, UpdateMarketplaceListingRequest>({
            query: (body) => {
                const formData = new FormData();
                const { listing_id, ...rest } = body;
                Object.keys(rest).forEach(key => {
                    if (key === 'images' && rest.images) {
                        rest.images.forEach((file: File) => {
                            formData.append('images', file);
                        });
                    } else if (key === 'remove_image_ids' && rest.remove_image_ids) {
                        rest.remove_image_ids.forEach((id: number) => {
                            formData.append('remove_image_ids[]', String(id));
                        });
                    } else if (key === 'tags' && rest.tags) {
                        rest.tags.forEach((tag: string) => {
                            formData.append('tags[]', tag);
                        });
                    } else if (key === 'car_details' && rest.car_details) {
                        // Always send car_details, even if empty object
                        const carDetails = rest.car_details;
                        if (Object.keys(carDetails).length > 0) {
                            Object.keys(carDetails).forEach(carKey => {
                                formData.append(`car_details[${carKey}]`, carDetails[carKey]);
                            });
                        } else {
                            // Send empty object as JSON string to ensure backend receives an object
                            formData.append('car_details', JSON.stringify({}));
                        }
                    } else if (key === 'house_details' && rest.house_details) {
                        // Always send house_details, even if empty object
                        const houseDetails = rest.house_details;
                        if (Object.keys(houseDetails).length > 0) {
                            Object.keys(houseDetails).forEach(houseKey => {
                                formData.append(`house_details[${houseKey}]`, houseDetails[houseKey]);
                            });
                        } else {
                            // Send empty object as JSON string to ensure backend receives an object
                            formData.append('house_details', JSON.stringify({}));
                        }
                    } else if (rest[key as keyof typeof rest] !== undefined) {
                        formData.append(key, String(rest[key as keyof typeof rest]));
                    }
                });
                formData.append('listing_id', String(listing_id));
                return {
                    url: '/admin/marketplace/listings',
                    method: 'PUT',
                    body: formData,
                };
            },
            invalidatesTags: ['MarketplaceListings'],
        }),

        updateMarketplaceListingStatus: builder.mutation<{ success: boolean; status: number; message: string; data: { listing_id: number; status: string } }, UpdateMarketplaceListingStatusRequest>({
            query: (body) => ({
                url: '/admin/marketplace/listings/status',
                method: 'PATCH',
                body,
            }),
            invalidatesTags: ['MarketplaceListings'],
        }),

        deleteMarketplaceListing: builder.mutation<{ success: boolean; status: number; message: string }, number>({
            query: (listing_id) => ({
                url: `/admin/marketplace/listings/${listing_id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['MarketplaceListings'],
        }),

        // Marketplace Inquiries
        getMarketplaceInquiries: builder.query<MarketplaceInquiriesResponse, MarketplaceInquiriesParams>({
            query: (params) => ({
                url: '/admin/marketplace/inquiries',
                params: {
                    page: params.page || 1,
                    limit: params.limit || 10,
                    ...(params.status && params.status !== 'all' && { status: params.status }),
                },
            }),
            providesTags: ['MarketplaceInquiries'],
        }),

        respondMarketplaceInquiry: builder.mutation<{ success: boolean; status: number; message: string; data: { inquiry_id: number; status: string } }, RespondMarketplaceInquiryRequest>({
            query: (body) => ({
                url: '/admin/marketplace/inquiries/respond',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['MarketplaceInquiries'],
        }),

        // Marketplace Transactions
        getMarketplaceTransactions: builder.query<MarketplaceTransactionsResponse, MarketplaceTransactionsParams>({
            query: (params) => ({
                url: '/admin/marketplace/transactions',
                params: {
                    page: params.page || 1,
                    limit: params.limit || 10,
                    ...(params.status && params.status !== 'all' && { status: params.status }),
                    ...(params.payment_method && { payment_method: params.payment_method }),
                },
            }),
            providesTags: ['MarketplaceTransactions'],
        }),

        updateMarketplaceTransactionStatus: builder.mutation<{ success: boolean; status: number; message: string; data: { transaction_id: number; status: string } }, UpdateMarketplaceTransactionStatusRequest>({
            query: (body) => ({
                url: '/admin/marketplace/transactions/update-status',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['MarketplaceTransactions', 'MarketplaceListings'],
        }),

        // Marketplace Settings
        getMarketplaceSettings: builder.query<MarketplaceSettingsResponse, void>({
            query: () => '/admin/marketplace/settings',
            providesTags: ['MarketplaceSettings'],
        }),

        updateMarketplaceSettings: builder.mutation<MarketplaceSettingsResponse, UpdateMarketplaceSettingsRequest>({
            query: (body) => ({
                url: '/admin/marketplace/settings',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['MarketplaceSettings'],
        }),

        // Marketplace KYC Requests
        getMarketplaceKycRequests: builder.query<MarketplaceKycRequestsResponse, MarketplaceKycRequestsParams>({
            query: (params) => ({
                url: '/admin/marketplace/kyc-requests',
                params: {
                    page: params.page || 1,
                    limit: params.limit || 10,
                    ...(params.status && params.status !== 'all' && { status: params.status }),
                },
            }),
            providesTags: ['MarketplaceKycRequests'],
        }),

        updateMarketplaceKycStatus: builder.mutation<{ success: boolean; status: number; message: string; data: { kyc_request_id: number; status: string } }, UpdateMarketplaceKycStatusRequest>({
            query: (body) => ({
                url: '/admin/marketplace/kyc-requests/update',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['MarketplaceKycRequests'],
        }),

        // Dealer Management
        getDealers: builder.query<DealersResponse, DealersParams>({
            query: (params) => ({
                url: '/admin/dealers',
                params: {
                    page: params.page || 1,
                    limit: params.limit || 10,
                    ...(params.verification_status && params.verification_status !== 'all' && { verification_status: params.verification_status }),
                    ...(params.dealer_type && params.dealer_type !== 'all' && { dealer_type: params.dealer_type }),
                    ...(params.search && { search: params.search }),
                    ...(params.deleted_status && { deleted_status: params.deleted_status }),
                },
            }),
            providesTags: ['Dealers'],
        }),

        bulkDeleteDealers: builder.mutation<{ success: boolean; message: string }, { dealer_ids: number[]; delete_type: 'soft' | 'hard' }>({
            query: (body) => ({
                url: '/admin/dealers/bulk-delete',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Dealers', 'AdminDashboard'],
        }),

        restoreDealer: builder.mutation<{ success: boolean; message: string }, { dealer_id: number }>({
            query: (body) => ({
                url: '/admin/dealers/restore',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Dealers'],
        }),


        getPendingDealers: builder.query<PendingDealersResponse, PendingDealersParams>({
            query: (params) => ({
                url: '/admin/dealers/pending',
                params: {
                    page: params.page || 1,
                    limit: params.limit || 10,
                },
            }),
            providesTags: ['Dealers'],
        }),

        getDealerStats: builder.query<DealerStatsResponse, void>({
            query: () => '/admin/dealers/stats',
            providesTags: ['Dealers'],
        }),

        getDealerDetails: builder.query<DealerDetailsResponse, number>({
            query: (dealer_id) => `/admin/dealers/${dealer_id}`,
            providesTags: ['Dealers'],
        }),

        getDealerDocuments: builder.query<DealerDocumentsResponse, number>({
            query: (dealer_id) => `/admin/dealers/${dealer_id}/documents`,
            providesTags: ['Dealers'],
        }),

        verifyDealer: builder.mutation<{ success: boolean; status: number; message: string; data: { dealer_id: number; verification_status: string; verified_at: string } }, VerifyDealerRequest>({
            query: (body) => ({
                url: '/admin/dealers/verify',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Dealers'],
        }),

        updateDealerStatus: builder.mutation<{ success: boolean; status: number; message: string; data: { dealer_id: number; is_active: boolean } }, UpdateDealerStatusRequest>({
            query: (body) => ({
                url: '/admin/dealers/update-status',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Dealers'],
        }),

        // User-facing Marketplace Endpoints
        getUserMarketplaceListings: builder.query<MarketplaceListingsResponse, UserMarketplaceListingParams>({
            query: (params) => ({
                url: '/api/marketplace/listings',
                params: {
                    page: params.page || 1,
                    limit: params.limit || 10,
                    ...(params.listing_type && { listing_type: params.listing_type }),
                    ...(params.min_price && { min_price: params.min_price }),
                    ...(params.max_price && { max_price: params.max_price }),
                    ...(params.search && { search: params.search }),
                    ...(params.location && { location: params.location }),
                    ...(params.featured !== undefined && { featured: params.featured }),
                    ...(params.listing_source && { listing_source: params.listing_source }),
                    ...(params.manufacturer && { manufacturer: params.manufacturer }),
                    ...(params.model && { model: params.model }),
                },
            }),
            providesTags: ['MarketplaceListings'],
        }),

        getUserMarketplaceListing: builder.query<MarketplaceListingResponse, number>({
            query: (listing_id) => `/api/marketplace/listings/${listing_id}`,
            providesTags: ['MarketplaceListings'],
        }),

        createMarketplaceInquiry: builder.mutation<{ success: boolean; status: number; message: string; data: { inquiry_id: number; status: string } }, CreateMarketplaceInquiryRequest>({
            query: (body) => ({
                url: '/api/marketplace/inquiries',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['MarketplaceInquiries'],
        }),

        getUserMarketplaceInquiries: builder.query<MarketplaceInquiriesResponse, UserMarketplaceInquiriesParams>({
            query: (params) => ({
                url: '/api/marketplace/inquiries',
                params: {
                    page: params.page || 1,
                    limit: params.limit || 10,
                    ...(params.status && { status: params.status }),
                },
            }),
            providesTags: ['MarketplaceInquiries'],
        }),

        getMarketplacePaymentOptions: builder.query<MarketplacePaymentOptionsResponse, void>({
            query: () => '/api/marketplace/payment-options',
            providesTags: ['MarketplaceSettings'],
        }),

        // Public sold listings endpoint (no authentication required)
        getPublicSoldListings: builder.query<MarketplaceListingsResponse, { page?: number; limit?: number; listing_type?: 'car' | 'house' | 'all' }>({
            query: (params) => ({
                url: '/api/marketplace/listings/sold',
                params: {
                    page: params.page || 1,
                    limit: params.limit || 12,
                    ...(params.listing_type && params.listing_type !== 'all' && { listing_type: params.listing_type }),
                },
            }),
            providesTags: ['MarketplaceListings'],
        }),

        purchaseMarketplaceListing: builder.mutation<PurchaseMarketplaceListingResponse, PurchaseMarketplaceListingRequest>({
            query: (body) => {
                const formData = new FormData();
                formData.append('listing_id', body.listing_id.toString());
                formData.append('amount', body.amount.toString());
                formData.append('payment_method', body.payment_method);
                if (body.wallet_amount !== undefined) {
                    formData.append('wallet_amount', body.wallet_amount.toString());
                }
                if (body.transaction_reference) {
                    formData.append('transaction_reference', body.transaction_reference);
                }
                if (body.notes) {
                    formData.append('notes', body.notes);
                }
                if (body.payment_screenshot) {
                    formData.append('payment_screenshot', body.payment_screenshot);
                }
                return {
                    url: '/api/marketplace/purchase',
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: ['MarketplaceTransactions', 'MarketplaceListings'],
        }),

        getUserMarketplaceTransactions: builder.query<MarketplaceTransactionsResponse, MarketplaceTransactionsParams>({
            query: (params) => ({
                url: '/api/marketplace/transactions',
                params: {
                    page: params.page || 1,
                    limit: params.limit || 10,
                    ...(params.status && params.status !== 'all' && { status: params.status }),
                    ...(params.payment_method && { payment_method: params.payment_method }),
                },
            }),
            providesTags: ['MarketplaceTransactions'],
        }),

        uploadMarketplacePaymentProof: builder.mutation<{ success: boolean; status: number; message: string; data: { transaction_id: number; payment_screenshot: string } }, UploadPaymentProofRequest>({
            query: (body) => {
                const formData = new FormData();
                formData.append('transaction_id', body.transaction_id.toString());
                formData.append('payment_screenshot', body.payment_screenshot);
                return {
                    url: '/api/marketplace/transactions/payment-proof',
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: ['MarketplaceTransactions'],
        }),

        getMarketplaceKycStatus: builder.query<MarketplaceKycStatusResponse, void>({
            query: () => '/api/marketplace/kyc/status',
            providesTags: ['MarketplaceKycRequests'],
        }),

        submitMarketplaceKyc: builder.mutation<{ success: boolean; status: number; message: string; data: { kyc_request_id: number; status: string } }, SubmitMarketplaceKycRequest>({
            query: (body) => {
                const formData = new FormData();
                formData.append('document_type', body.document_type);
                formData.append('document_front', body.document_front);
                if (body.document_back) {
                    formData.append('document_back', body.document_back);
                }
                formData.append('address_proof', body.address_proof);
                if (body.selfie_image) {
                    formData.append('selfie_image', body.selfie_image);
                }
                return {
                    url: '/api/marketplace/kyc/submit',
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: ['MarketplaceKycRequests'],
        }),

        // User-facing Membership & Booking Endpoints
        getPublicMembershipPlans: builder.query<MembershipPlanListResponse, void>({
            query: () => '/api/membership/plans',
            providesTags: ['MembershipPlans'],
        }),

        purchaseMembership: builder.mutation<PurchaseMembershipResponse, PurchaseMembershipRequest>({
            query: (body) => ({
                url: '/api/membership/purchase',
                method: 'POST',
                body: {
                    plan_id: body.plan_id,
                    payment_type: body.payment_type,
                },
            }),
            invalidatesTags: ['Memberships', 'Profile'],
        }),

        getUserMembershipStatus: builder.query<UserMembershipStatusResponse, void>({
            query: () => '/api/membership/status',
            providesTags: ['Memberships'],
        }),

        getUserApartments: builder.query<ApartmentListResponse, UserApartmentListParams>({
            query: (params) => ({
                url: '/api/apartments',
                params: {
                    page: params.page || 1,
                    limit: params.limit || 12,
                    ...(params.location && { location: params.location }),
                    ...(params.capacity && { capacity: params.capacity }),
                    ...(params.search && { search: params.search }),
                },
            }),
            providesTags: ['Apartments'],
        }),

        getUserApartment: builder.query<ApartmentResponse, number>({
            query: (apartment_id) => `/api/apartments/${apartment_id}`,
            providesTags: ['Apartments'],
        }),

        createBooking: builder.mutation<CreateBookingResponse, CreateBookingRequest>({
            query: (body) => ({
                url: '/api/bookings/create',
                method: 'POST',
                body: {
                    apartment_id: body.apartment_id,
                    check_in_date: body.check_in_date,
                    check_out_date: body.check_out_date,
                    guests: body.guests,
                },
            }),
            invalidatesTags: ['Bookings', 'Apartments', 'Memberships'],
        }),

        getUserBookings: builder.query<BookingListResponse, UserBookingListParams>({
            query: (params) => ({
                url: '/api/bookings/my-bookings',
                params: {
                    ...(params.status && params.status !== 'all' && { status: params.status }),
                },
            }),
            providesTags: ['Bookings'],
        }),

        cancelBooking: builder.mutation<BookingResponse, CancelBookingRequest>({
            query: (body) => ({
                url: '/api/bookings/cancel',
                method: 'POST',
                body: {
                    booking_id: body.booking_id,
                    reason: body.reason,
                },
            }),
            invalidatesTags: ['Bookings', 'Memberships'],
        }),

        // User-facing Child Sponsorship Endpoints
        getSupportedCurrencies: builder.query<SupportedCurrenciesResponse, void>({
            query: () => '/api/children/supported-currencies',
            providesTags: ['ChildSponsorship'],
        }),

        getAvailableChildren: builder.query<AvailableChildrenResponse, AvailableChildrenParams>({
            query: (params) => ({
                url: '/api/children/available',
                params: {
                    page: params.page || 1,
                    limit: params.limit || 10,
                    ...(params.age_min && { age_min: params.age_min }),
                    ...(params.age_max && { age_max: params.age_max }),
                    ...(params.location && { location: params.location }),
                    ...(params.education_stage && { education_stage: params.education_stage }),
                    ...(params.filter_currency !== undefined && { filter_currency: params.filter_currency }),
                },
            }),
            providesTags: ['ChildSponsorship'],
        }),

        getUserChildDetails: builder.query<ChildDetailsResponse, number>({
            query: (child_id) => `/api/children/${child_id}`,
            providesTags: ['ChildSponsorship'],
        }),

        sponsorChild: builder.mutation<SponsorChildResponse, SponsorChildRequest>({
            query: (body) => ({
                url: `/api/children/${body.child_id}/sponsor`,
                method: 'POST',
                body: {
                    child_id: body.child_id,
                    sponsorship_type: body.sponsorship_type,
                    amount: body.amount,
                    auto_renewal: body.auto_renewal !== undefined ? body.auto_renewal : true,
                    notes: body.notes,
                },
            }),
            invalidatesTags: ['ChildSponsorship', 'Profile'],
        }),

        getSponsoredChildren: builder.query<SponsoredChildrenResponse, SponsoredChildrenParams>({
            query: (params) => ({
                url: '/api/sponsored-children',
                params: {
                    page: params.page || 1,
                    limit: params.limit || 10,
                    ...(params.status && params.status !== 'all' && { status: params.status }),
                },
            }),
            providesTags: ['ChildSponsorship'],
        }),

        getUserSponsorshipUpdates: builder.query<SponsorshipUpdatesResponse, { child_id: number } & SponsorshipUpdatesParams>({
            query: ({ child_id, ...params }) => ({
                url: `/api/sponsored-children/${child_id}/updates`,
                params: {
                    page: params.page || 1,
                    limit: params.limit || 10,
                    ...(params.update_type && { update_type: params.update_type }),
                },
            }),
            providesTags: ['ChildSponsorshipUpdates'],
        }),

        updateSponsorship: builder.mutation<UpdateSponsorshipResponse, UpdateSponsorshipRequest>({
            query: (body) => ({
                url: '/api/sponsorship/update',
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['ChildSponsorship'],
        }),

        getUserSponsorshipStats: builder.query<UserSponsorshipStatsResponse, void>({
            query: () => '/api/sponsorship/stats',
            providesTags: ['ChildSponsorship'],
        }),

        // User-facing Wallet Endpoints
        getUserWalletBalance: builder.query<UserWalletBalanceResponse, void>({
            query: () => '/api/wallet/balance',
            providesTags: ['Profile', 'WalletTransactions'],
        }),

        getUserWalletTransactions: builder.query<UserWalletTransactionsResponse, UserWalletTransactionsParams>({
            query: (params) => ({
                url: '/api/wallet/transactions',
                params: {
                    page: params.page || 1,
                    limit: params.limit || 10,
                    ...(params.transaction_type && params.transaction_type !== 'all' && { transaction_type: params.transaction_type }),
                },
            }),
            providesTags: ['WalletTransactions'],
        }),

        addFundsToWallet: builder.mutation<AddFundsToWalletResponse, AddFundsToWalletRequest>({
            query: (body) => {
                const formData = new FormData();
                formData.append('amount', String(body.amount));
                formData.append('payment_method', body.payment_method);
                if (body.transaction_reference) {
                    formData.append('transaction_reference', body.transaction_reference);
                }
                if (body.description) {
                    formData.append('description', body.description);
                }
                formData.append('payment_screenshot', body.payment_screenshot);
                return {
                    url: '/api/wallet/add-funds',
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: ['WalletTransactions', 'Profile'],
        }),

        withdrawFundsFromWallet: builder.mutation<WithdrawFundsResponse, WithdrawFundsRequest>({
            query: (body) => ({
                url: '/api/wallet/withdraw',
                method: 'POST',
                body: {
                    amount: body.amount,
                    ...(body.description && { description: body.description }),
                },
            }),
            invalidatesTags: ['WalletTransactions', 'Profile'],
        }),

        getUserWalletStats: builder.query<UserWalletStatsResponse, void>({
            query: () => '/api/wallet/stats',
            providesTags: ['WalletStats'],
        }),

        // Payment Methods Endpoints
        getActivePaymentMethods: builder.query<PaymentMethodsResponse, void>({
            query: () => '/api/payment-methods',
            providesTags: [],
        }),

        getPaymentMethodsList: builder.query<PaymentMethodsResponse, void>({
            query: () => '/api/payment-methods/list',
            providesTags: [],
        }),

        getPaymentMethodDetails: builder.query<PaymentMethodDetailsResponse, string>({
            query: (method_name) => `/api/payment-methods/${method_name}`,
            providesTags: [],
        }),

        // Dealer Auth Endpoints
        registerDealer: builder.mutation<{ success: boolean; status: number; message: string; data: { dealer_id: number; email: string; verification_status: string } }, FormData>({
            query: (formData) => ({
                url: '/api/dealer/register',
                method: 'POST',
                body: formData,
            }),
        }),

        dealerSendOTP: builder.mutation<{ success: boolean; status: number; message: string; data: { otp: string } }, { email: string }>({
            query: (body) => ({
                url: '/api/dealer/login-with-email',
                method: 'POST',
                body,
            }),
        }),

        dealerLoginWithOTP: builder.mutation<DealerLoginResponse, { email: string; otp: string }>({
            query: (body) => ({
                url: '/api/dealer/login-with-otp',
                method: 'POST',
                body,
            }),
        }),

        getDealerProfile: builder.query<DealerProfileResponse, void>({
            query: () => '/api/dealer/profile',
            providesTags: ['DealerProfile'],
        }),

        updateDealerProfile: builder.mutation<DealerProfileResponse, FormData>({
            query: (formData) => ({
                url: '/api/dealer/profile',
                method: 'PUT',
                body: formData,
            }),
            invalidatesTags: ['DealerProfile'],
        }),

        updateDealerDocuments: builder.mutation<{ success: boolean; status: number; message: string; data: { dealer_id: number; verification_status: string; documents: Array<{ document_id: number; document_type: string; document_path: string; document_name: string }> } }, FormData>({
            query: (formData) => ({
                url: '/api/dealer/documents',
                method: 'PUT',
                body: formData,
            }),
            invalidatesTags: ['DealerProfile'],
        }),

        resubmitDealerVerification: builder.mutation<{ success: boolean; status: number; message: string; data: { dealer_id: number; verification_status: string } }, void>({
            query: () => ({
                url: '/api/dealer/resubmit',
                method: 'POST',
            }),
            invalidatesTags: ['DealerProfile'],
        }),

        getDealerDashboardStats: builder.query<DealerDashboardStatsResponse, void>({
            query: () => '/api/dealer/dashboard/stats',
            providesTags: ['DealerDashboard'],
        }),

        // Dealer Listing Endpoints
        getDealerListings: builder.query<DealerListingsResponse, DealerListingsParams>({
            query: (params) => ({
                url: '/api/dealer/listings',
                params: {
                    page: params.page || 1,
                    limit: params.limit || 10,
                    ...(params.status && params.status !== 'all' && { status: params.status }),
                    ...(params.approval_status && params.approval_status !== 'all' && { approval_status: params.approval_status }),
                },
            }),
            providesTags: ['DealerListings'],
        }),

        getDealerListing: builder.query<DealerListingResponse, number>({
            query: (listing_id) => `/api/dealer/listings/${listing_id}`,
            providesTags: ['DealerListings'],
        }),

        createDealerListing: builder.mutation<{ success: boolean; status: number; message: string; data: { listing_id: number; title: string; approval_status: string } }, FormData>({
            query: (formData) => ({
                url: '/api/dealer/listings',
                method: 'POST',
                body: formData,
            }),
            invalidatesTags: ['DealerListings', 'DealerDashboard'],
        }),

        updateDealerListing: builder.mutation<{ success: boolean; status: number; message: string; data: { listing_id: number; approval_status: string } }, FormData>({
            query: (formData) => ({
                url: '/api/dealer/listings',
                method: 'PUT',
                body: formData,
            }),
            invalidatesTags: ['DealerListings', 'DealerDashboard'],
        }),

        deleteDealerListing: builder.mutation<{ success: boolean; status: number; message: string }, number>({
            query: (listing_id) => ({
                url: `/api/dealer/listings/${listing_id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['DealerListings', 'DealerDashboard'],
        }),

        getDealerSoldListings: builder.query<DealerListingsResponse, { page?: number; limit?: number }>({
            query: (params) => ({
                url: '/api/dealer/listings/sold',
                params: {
                    page: params.page || 1,
                    limit: params.limit || 10,
                },
            }),
            providesTags: ['DealerListings'],
        }),

        markDealerListingSold: builder.mutation<{ success: boolean; status: number; message: string; data: { listing_id: number; status: string } }, number>({
            query: (listing_id) => ({
                url: `/api/dealer/listings/${listing_id}/mark-sold`,
                method: 'POST',
            }),
            invalidatesTags: ['DealerListings', 'DealerDashboard'],
        }),

        // Jobs endpoints
        getPublicJobs: builder.query<JobsResponse, { location?: string; type?: string; department?: string }>({
            query: (params) => {
                const searchParams = new URLSearchParams();
                if (params.location) searchParams.append('location', params.location);
                if (params.type) searchParams.append('type', params.type);
                if (params.department) searchParams.append('department', params.department);
                const queryString = searchParams.toString();
                return `/api/jobs${queryString ? `?${queryString}` : ''}`;
            },
            providesTags: ['Jobs'],
        }),
        getJobById: builder.query<JobResponse, number>({
            query: (id) => `/api/jobs/${id}`,
            providesTags: (_result, _error, id) => [{ type: 'Jobs', id }],
        }),
        applyForJob: builder.mutation<{ success: boolean; message: string }, FormData>({
            query: (formData) => ({
                url: '/api/jobs/apply',
                method: 'POST',
                body: formData,
            }),
            invalidatesTags: ['JobApplications'],
        }),
        getAdminJobs: builder.query<JobsResponse, void>({
            query: () => '/admin/jobs',
            providesTags: ['Jobs'],
        }),
        createJob: builder.mutation<JobResponse, Partial<Job>>({
            query: (body) => ({
                url: '/admin/jobs/create',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Jobs'],
        }),
        updateJob: builder.mutation<JobResponse, { id: number; data: Partial<Job> }>({
            query: ({ id, data }) => ({
                url: `/admin/jobs/update/${id}`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Jobs'],
        }),
        deleteJob: builder.mutation<{ success: boolean; message: string }, number>({
            query: (id) => ({
                url: `/admin/jobs/delete/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Jobs'],
        }),
        getJobApplications: builder.query<JobApplicationsResponse, { jobId?: number }>({
            query: (params) => {
                const url = params?.jobId ? `/admin/job-applications/${params.jobId}` : '/admin/job-applications';
                return url;
            },
            providesTags: ['JobApplications'],
        }),
    }),
});


// Export hooks
export const {
    useAdminLoginMutation,
    useGetAdminDashboardDetailsQuery,
    useGetSuperAdminDashboardDetailsQuery,
    useGetAdminUserListQuery,
    useUpdateUserStatusMutation,
    useRestoreUserMutation,
    useBulkDeleteUsersMutation,
    useGetAdminUserQuery,
    useDeleteAdminUserMutation,
    useGetAllCategoriesQuery,
    useCreateCategoryMutation,
    useUpdateCategoryMutation,
    useDeleteCategoryMutation,
    useGetAllBankAccountsQuery,
    useCreateBankAccountMutation,
    useUpdateBankAccountMutation,
    useDeleteBankAccountMutation,
    useGetAllAdminsQuery,
    useCreateAdminMutation,
    useUpdateAdminMutation,
    useDeleteAdminMutation,
    useGetAllInvestmentsQuery,
    useApproveRejectInvestmentMutation,
    useCreateProjectStep1Mutation,
    useUpdateProjectMutation,
    useAddProjectImagesMutation,
    useAddProjectDocumentsMutation,
    useDeleteProjectMutation,
    useBulkDeleteProjectsMutation,
    useDeleteProjectDocumentMutation,
    useDeleteProjectImageMutation,
    useSearchLocationQuery,
    useGetLatLongQuery,
    useGetProjectDetailsQuery,
    useGetBlogsQuery,
    useCreateBlogMutation,
    useUpdateBlogMutation,
    useDeleteBlogMutation,
    useGetInvestmentsQuery,
    useCreateInvestmentMutation,
    useGetMyInvestmentsQuery,
    useGetMyInvestmentStatsQuery,
    useCreateWalletInvestmentMutation,
    useGetAllProjectsQuery,
    useGetProjectStatusQuery,
    useGetProfileQuery,
    useUpdateProfileMutation,
    useUpdateSuperAdminProfileMutation,
    useUpdateUserAccountDetailsMutation,
    useGetNotificationsQuery,
    useScheduleNotificationMutation,
    useUpdateNotificationMutation,
    useDeleteNotificationMutation,
    useGetUserDetailsQuery,
    useGetCTAPopupsQuery,
    useCreateCTAPopupMutation,
    useUpdateCTAPopupMutation,
    useDeleteCTAPopupMutation,
    useGetContactSubmissionsQuery,
    useGetContactSubmissionQuery,
    useCreateContactSubmissionMutation,
    useUpdateContactSubmissionMutation,
    useDeleteContactSubmissionMutation,
    useMarkContactAsReadMutation,
    useReplyToContactMutation,
    useGetContactStatisticsQuery,
    useCreateSolarLeadMutation,
    useGetSolarLeadsQuery,
    useLazyGetSolarLeadsQuery,
    useGetSolarLeadQuery,
    useUpdateSolarLeadStatusMutation,
    useScheduleSolarMeetingMutation,
    useUpdateSolarMeetingMutation,
    useUpdateSolarLeadNotesMutation,
    useDeleteSolarLeadMutation,
    useGetPublicBlogsQuery,
    useGetFeaturedBlogsQuery,
    useGetBlogBySlugQuery,
    useGetPublicProjectsQuery,
    useGetPublicStatisticsQuery,
    useGetTestimonialsQuery,
    useCreateTestimonialMutation,
    useUpdateTestimonialMutation,
    useUpdateTestimonialStatusMutation,
    useToggleTestimonialFeaturedMutation,
    useDeleteTestimonialMutation,
    useGetPublicTestimonialsQuery,
    useGetWalletTransactionsQuery,
    useApproveRejectWalletTransactionMutation,
    useAddFundsMutation,
    useGetWalletStatsQuery,
    useAddProjectDocumentMutation,
    // Child Sponsorship hooks
    useGetAllChildrenQuery,
    useGetChildDetailsQuery,
    useCreateChildMutation,
    useUpdateChildMutation,
    useVerifyChildMutation,
    useDeleteChildMutation,
    useGetSponsorshipUpdatesQuery,
    useCreateSponsorshipUpdateMutation,
    useUpdateSponsorshipUpdateMutation,
    useDeleteSponsorshipUpdateMutation,
    // Membership Plans hooks
    useGetAllMembershipPlansQuery,
    useGetMembershipPlanQuery,
    useCreateMembershipPlanMutation,
    useUpdateMembershipPlanMutation,
    useDeleteMembershipPlanMutation,
    useGetAllApartmentsQuery,
    useGetApartmentQuery,
    useCreateApartmentMutation,
    useUpdateApartmentMutation,
    useDeleteApartmentMutation,
    useDeleteApartmentImageMutation,
    useManageApartmentAvailabilityMutation,
    useGetAllBookingsQuery,
    useGetBookingDetailsQuery,
    useUpdateBookingStatusMutation,
    useProcessRefundMutation,
    useGetAllMembershipsQuery,
    useUpdateMembershipStatusMutation,
    // Marketplace hooks
    useGetMarketplaceListingsQuery,
    useGetMarketplaceListingQuery,
    useGetSoldListingsQuery,
    useGetPendingListingsQuery,
    useApproveListingMutation,
    useRejectListingMutation,
    useCreateMarketplaceListingMutation,
    useUpdateMarketplaceListingMutation,
    useUpdateMarketplaceListingStatusMutation,
    useDeleteMarketplaceListingMutation,
    useGetMarketplaceInquiriesQuery,
    useRespondMarketplaceInquiryMutation,
    useGetMarketplaceTransactionsQuery,
    useUpdateMarketplaceTransactionStatusMutation,
    useGetMarketplaceSettingsQuery,
    useUpdateMarketplaceSettingsMutation,
    useGetMarketplaceKycRequestsQuery,
    useUpdateMarketplaceKycStatusMutation,
    // User-facing Marketplace hooks
    useGetUserMarketplaceListingsQuery,
    useGetUserMarketplaceListingQuery,
    useCreateMarketplaceInquiryMutation,
    useGetUserMarketplaceInquiriesQuery,
    useGetMarketplacePaymentOptionsQuery,
    usePurchaseMarketplaceListingMutation,
    useGetUserMarketplaceTransactionsQuery,
    useUploadMarketplacePaymentProofMutation,
    useGetMarketplaceKycStatusQuery,
    useSubmitMarketplaceKycMutation,
    useGetPublicSoldListingsQuery,
    // User-facing Membership & Booking hooks
    useGetPublicMembershipPlansQuery,
    usePurchaseMembershipMutation,
    useGetUserMembershipStatusQuery,
    useGetUserApartmentsQuery,
    useGetUserApartmentQuery,
    useCreateBookingMutation,
    useGetUserBookingsQuery,
    useCancelBookingMutation,
    useGetSupportedCurrenciesQuery,
    useGetAvailableChildrenQuery,
    useGetUserChildDetailsQuery,
    useSponsorChildMutation,
    useGetSponsoredChildrenQuery,
    useGetUserSponsorshipUpdatesQuery,
    useUpdateSponsorshipMutation,
    useGetUserSponsorshipStatsQuery,
    // User-facing Wallet hooks
    useGetUserWalletBalanceQuery,
    useGetUserWalletTransactionsQuery,
    useAddFundsToWalletMutation,
    useWithdrawFundsFromWalletMutation,
    useGetUserWalletStatsQuery,
    // Payment Methods hooks
    useGetActivePaymentMethodsQuery,
    useGetPaymentMethodsListQuery,
    useGetPaymentMethodDetailsQuery,
    useGetDealersQuery,
    useGetPendingDealersQuery,
    useGetDealerStatsQuery,
    useGetDealerDetailsQuery,
    useGetDealerDocumentsQuery,
    useVerifyDealerMutation,
    useUpdateDealerStatusMutation,
    useBulkDeleteDealersMutation,
    useRestoreDealerMutation,
    // Dealer Auth hooks
    useRegisterDealerMutation,
    useDealerSendOTPMutation,
    useDealerLoginWithOTPMutation,
    useGetDealerProfileQuery,
    useUpdateDealerProfileMutation,
    useUpdateDealerDocumentsMutation,
    useResubmitDealerVerificationMutation,
    useGetDealerDashboardStatsQuery,
    // Dealer Listing hooks
    useGetDealerListingsQuery,
    useGetDealerListingQuery,
    useCreateDealerListingMutation,
    useUpdateDealerListingMutation,
    useDeleteDealerListingMutation,
    useGetDealerSoldListingsQuery,
    useMarkDealerListingSoldMutation,
    // Jobs hooks
    useGetPublicJobsQuery,
    useGetJobByIdQuery,
    useApplyForJobMutation,
    useGetAdminJobsQuery,
    useCreateJobMutation,
    useUpdateJobMutation,
    useDeleteJobMutation,
    useGetJobApplicationsQuery,
    useGetRoleConfigQuery,
    useGetRoleDefaultsQuery,
    useUpdateAdminRbacMutation,
    useUpdateAdminStatusMutation,
} = adminApi;

